import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AuthorizedShopReviewAnalysis,
  GeneratedImage,
  ImageGenerationRequest,
  ImageProviderId,
  ImageProviderInput,
  PublicProductReviewAnalysis,
  ReviewLibraryItem,
} from "@jianze/contracts";
import JSZip from "jszip";
import {
  Camera,
  Check,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clipboard,
  ClipboardPaste,
  Database,
  Download,
  ExternalLink,
  Eye,
  FileSearch,
  FolderOpen,
  History,
  Image,
  Images,
  KeyRound,
  Link2,
  Pause,
  Pencil,
  Play,
  PlugZap,
  Plus,
  RefreshCw,
  Save,
  Search,
  Settings2,
  Sparkles,
  Square,
  Store,
  Trash2,
  UserRound,
  WandSparkles,
  X,
  ZoomIn,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { dashboardApi } from "../api";
import { prepareImageGenerationReferences } from "../image-generation-references";
import "./ReviewStudioPage.css";
import "./ReviewStudioPaste.css";
import "./ReviewStudioReadability.css";
import "./ReviewStudioProviders.css";
import "./ReviewStudioGeneration.css";

type Branch = "analysis" | "reviews" | "images" | "history" | "database";
type Direction =
  | "buyer"
  | "model"
  | "effect"
  | "scene"
  | "comparison"
  | "retail";
export type ReviewEndorsementCountry = "英国" | "日本" | "韩国";
export interface ReviewEndorsementSettings {
  mentionEmbassy: boolean;
  mentionCelebrity: boolean;
  country: ReviewEndorsementCountry;
}
export type ReviewRunPhase =
  | "idle"
  | "preflight"
  | "running"
  | "paused"
  | "stopped"
  | "partial"
  | "failed"
  | "completed";
type DirectionContent = Record<Direction, { title: string; detail: string }>;
export const authorizedShopReviewTitle = "拼多多授权店铺评价分析";
export const authorizedShopReviewDescription =
  "读取当前已授权店铺近 30 天评价，与公开商品评价分开";
export const publicProductReviewTitle = "拼多多公开商品评价分析";
export const publicProductReviewDescription =
  "粘贴拼多多商品链接，读取平台公开评价并提炼主题";
export function reviewReferenceThemes(
  publicAnalysis: PublicProductReviewAnalysis | null,
  authorizedAnalysis: AuthorizedShopReviewAnalysis | null,
) {
  return (publicAnalysis ?? authorizedAnalysis)?.themes.map((theme) => theme.name) ?? [];
}
interface ProductAsset {
  id: string;
  name: string;
  url: string;
  size: number;
}
interface ReferenceLink {
  id: string;
  name: string;
  url: string;
  note: string;
}
interface ReviewImageSlot {
  id: string;
  direction: Direction;
  status: "waiting" | "generating" | "ready" | "error";
  image: GeneratedImage | null;
  error?: string | null;
  generationStartedAt?: number | null;
}
interface ReviewDraft {
  id: string;
  batchId?: string;
  text: string;
  createdAt: string;
  directions: Direction[];
  imageCount: number;
  imageSlots: ReviewImageSlot[];
  /** Stable identity instructions shared by every image slot in this review group. */
  modelIdentity?: string;
  /** Stable wardrobe instructions persisted with the model identity. */
  modelWardrobe?: string;
}
interface HistoryRecord {
  id: string;
  productName: string;
  brandName?: string;
  count: number;
  imageCount: number;
  createdAt: string;
  reviews: string[];
  status?: "running" | "paused" | "partial" | "failed" | "completed";
  category?: string;
  audience?: string;
  benefits?: string;
  assets?: ProductAsset[];
  referenceAssetIds?: string[];
  selectedDirections?: Direction[];
  authorized?: boolean;
  drafts?: ReviewDraft[];
  competitorUrl?: string;
  competitorAnalysis?: AuthorizedShopReviewAnalysis | null;
  publicProductReviewAnalysis?: PublicProductReviewAnalysis | null;
  reviewRequirements?: string;
  endorsementSettings?: ReviewEndorsementSettings;
  generationRequirements?: string;
  lockGroupModel?: boolean;
  directionRequirements?: Record<Direction, string>;
  directionContent?: DirectionContent;
}

export const reviewGenerationInterruptedMessage =
  "页面刷新或更新中断了这张图，系统没有自动重试，请手动继续";

export const reviewGenerationStoppedMessage =
  "任务已停止，系统没有自动重试，请手动继续";

export const reviewGenerationPausedMessage =
  "生成已暂停，系统没有自动重试，请选择继续生成或重新生成";

export function canStartPausedReviewGenerationAction(
  mutationPending: boolean,
  manuallyPaused: boolean,
) {
  return !mutationPending || manuallyPaused;
}

export function isCurrentReviewGenerationRun(
  activeRunId: number,
  candidateRunId: number,
) {
  return activeRunId === candidateRunId;
}

type ClipboardHtmlImageParser = (html: string) => string[];

function browserClipboardHtmlImages(html: string) {
  if (!html.trim() || typeof DOMParser === "undefined") return [];
  const document = new DOMParser().parseFromString(html, "text/html");
  return [...document.querySelectorAll("img")].flatMap((image) => {
    const source =
      image.getAttribute("src") ||
      image.getAttribute("data-src") ||
      image.getAttribute("data-original") ||
      image.getAttribute("data-lazy-src");
    return source ? [source] : [];
  });
}

export function clipboardImageSources(
  html: string,
  text: string,
  baseUrl: string,
  parseHtml: ClipboardHtmlImageParser = browserClipboardHtmlImages,
) {
  const textUrls = text.match(/https?:\/\/[^\s"'<>]+/gi) ?? [];
  const candidates = [...parseHtml(html), ...textUrls].map((value) =>
    value.trim().replace(/[),.;，。；]+$/, ""),
  );
  return [
    ...new Set(
      candidates.flatMap((value) => {
        try {
          const url = new URL(value, baseUrl);
          return ["http:", "https:", "data:", "blob:"].includes(url.protocol)
            ? [url.toString()]
            : [];
        } catch {
          return [];
        }
      }),
    ),
  ];
}

export function appendProductReferenceIds(
  currentIds: string[],
  addedIds: string[],
  maximum = 4,
) {
  return [
    ...currentIds,
    ...addedIds.filter((id) => !currentIds.includes(id)),
  ].slice(0, maximum);
}

export interface ReviewImageFailureDetail {
  draftId: string;
  slotId: string;
  draftIndex: number;
  slotIndex: number;
  direction: Direction;
  message: string;
}

export function reviewImageFailureDetails(
  drafts: ReviewDraft[],
): ReviewImageFailureDetail[] {
  return drafts.flatMap((draft, draftIndex) =>
    draft.imageSlots.flatMap((slot, slotIndex) => {
      const message = slot.error?.trim();
      return message
        ? [
            {
              draftId: draft.id,
              slotId: slot.id,
              draftIndex,
              slotIndex,
              direction: slot.direction,
              message,
            },
          ]
        : [];
    }),
  );
}

export function reviewImageElapsedSeconds(
  startedAt?: number | null,
  now = Date.now(),
) {
  if (!Number.isFinite(startedAt) || !Number.isFinite(now) || !startedAt) {
    return 0;
  }
  return Math.max(0, Math.floor((now - startedAt) / 1_000));
}

export function isReviewImageApproved(image?: GeneratedImage | null) {
  return Boolean(image);
}

export function restoreReviewDraftsForManualContinuation(
  drafts: ReviewDraft[],
): ReviewDraft[] {
  return drafts
    .filter((draft) => Array.isArray(draft.imageSlots))
    .map((draft) => ({
      ...draft,
      imageSlots: draft.imageSlots.map((slot) =>
        slot.image
          ? {
              ...slot,
              status: "ready" as const,
              error: null,
              generationStartedAt: null,
            }
          : slot.status === "generating"
            ? {
                ...slot,
                status: "waiting" as const,
                error: reviewGenerationInterruptedMessage,
                generationStartedAt: null,
              }
            : slot.status === "ready" || slot.status === "error"
              ? slot
              : {
                  ...slot,
                  status: "waiting" as const,
                  error: slot.error ?? null,
                  generationStartedAt: null,
                },
      ),
    }));
}

export function prepareReviewDraftsForManualContinuation(
  drafts: ReviewDraft[],
): ReviewDraft[] {
  return drafts.map((draft) => ({
    ...draft,
    imageSlots: draft.imageSlots.map((slot) =>
      slot.image
        ? {
            ...slot,
            status: "ready" as const,
            error: null,
            generationStartedAt: null,
          }
        : {
            ...slot,
            status: "waiting" as const,
            error: null,
            generationStartedAt: null,
          },
    ),
  }));
}

export function pauseReviewDraftGeneration(
  drafts: ReviewDraft[],
): ReviewDraft[] {
  return drafts.map((draft) => ({
    ...draft,
    imageSlots: draft.imageSlots.map((slot) =>
      slot.status === "generating" || slot.status === "waiting"
        ? {
            ...slot,
            status: "waiting" as const,
            error: reviewGenerationPausedMessage,
            generationStartedAt: null,
          }
        : slot,
    ),
  }));
}

export function markReviewDraftSlotsFailed(
  drafts: ReviewDraft[],
  message: string,
): ReviewDraft[] {
  return drafts.map((draft) => ({
    ...draft,
    imageSlots: draft.imageSlots.map((slot) =>
      slot.image
        ? {
            ...slot,
            status: "ready" as const,
            error: null,
            generationStartedAt: null,
          }
        : {
            ...slot,
            status: "error" as const,
            error: message,
            generationStartedAt: null,
          },
    ),
  }));
}

export function reviewDraftSlotSummary(drafts: ReviewDraft[]) {
  const slots = drafts.flatMap((draft) => draft.imageSlots);
  const ready = slots.filter((slot) => slot.status === "ready").length;
  const failed = slots.filter((slot) => slot.status === "error").length;
  const generating = slots.filter(
    (slot) => slot.status === "generating",
  ).length;
  return {
    total: slots.length,
    ready,
    failed,
    generating,
    remaining: Math.max(0, slots.length - ready),
  };
}

export function resolveReviewHistoryStatus(
  drafts: ReviewDraft[],
  phase: ReviewRunPhase,
): NonNullable<HistoryRecord["status"]> {
  const summary = reviewDraftSlotSummary(drafts);
  if (summary.total > 0 && summary.ready === summary.total) return "completed";
  if (phase === "preflight" || phase === "running") return "running";
  if (summary.failed > 0) return summary.ready > 0 ? "partial" : "failed";
  return "paused";
}
export interface ReviewWorkspaceSnapshot {
  branch: Branch;
  productName: string;
  brandName: string;
  category: string;
  audience: string;
  benefits: string;
  assets: ProductAsset[];
  referenceAssetIds: string[];
  selectedDirections: Direction[];
  imageCount: number;
  reviewCount: number;
  authorized: boolean;
  drafts: ReviewDraft[];
  competitorUrl: string;
  competitorAnalysis: AuthorizedShopReviewAnalysis | null;
  publicProductReviewAnalysis: PublicProductReviewAnalysis | null;
  reviewRequirements: string;
  endorsementSettings: ReviewEndorsementSettings;
  taskImageCount: number;
  references: ReferenceLink[];
  imageDirection: Direction;
  generatedImages: GeneratedImage[];
  generationRequirements: string;
  lockGroupModel?: boolean;
  directionRequirements: Record<Direction, string>;
  directionContent: DirectionContent;
  selectedDraftIds: string[];
}

export const defaultReviewEndorsementSettings: ReviewEndorsementSettings = {
  mentionEmbassy: false,
  mentionCelebrity: false,
  country: "英国",
};

export function restoreReviewEndorsementSettings(
  value: Partial<ReviewEndorsementSettings> | null | undefined,
): ReviewEndorsementSettings {
  const country = ["英国", "日本", "韩国"].includes(value?.country ?? "")
    ? (value!.country as ReviewEndorsementCountry)
    : defaultReviewEndorsementSettings.country;
  return {
    mentionEmbassy: value?.mentionEmbassy === true,
    mentionCelebrity: value?.mentionCelebrity === true,
    country,
  };
}

export function restoreReviewWorkspaceForRefresh(
  snapshot: Partial<ReviewWorkspaceSnapshot>,
): Partial<ReviewWorkspaceSnapshot> {
  const assets = Array.isArray(snapshot.assets) ? snapshot.assets : [];
  const availableAssetIds = new Set(assets.map((asset) => asset.id));
  const savedReferenceIds = Array.isArray(snapshot.referenceAssetIds)
    ? snapshot.referenceAssetIds.filter((id) => availableAssetIds.has(id))
    : [];
  const drafts = ensureReviewGroupModelIdentities(
    restoreReviewDraftsForManualContinuation(
      Array.isArray(snapshot.drafts) ? snapshot.drafts : [],
    ),
    snapshot.lockGroupModel !== false,
  );
  const availableDraftIds = new Set(drafts.map((draft) => draft.id));

  return {
    ...snapshot,
    competitorUrl:
      typeof snapshot.competitorUrl === "string" ? snapshot.competitorUrl : "",
    competitorAnalysis:
      snapshot.competitorAnalysis?.source === "pinduoduo-authorized-shop"
        ? snapshot.competitorAnalysis
        : null,
    publicProductReviewAnalysis:
      snapshot.publicProductReviewAnalysis?.source ===
      "pinduoduo-public-product"
        ? snapshot.publicProductReviewAnalysis
        : null,
    endorsementSettings: restoreReviewEndorsementSettings(
      snapshot.endorsementSettings,
    ),
    assets,
    referenceAssetIds: (savedReferenceIds.length
      ? savedReferenceIds
      : assets.map((asset) => asset.id)
    ).slice(0, 4),
    ...(Array.isArray(snapshot.selectedDirections)
      ? {
          selectedDirections: snapshot.selectedDirections.filter((direction) =>
            directions.some((item) => item.key === direction),
          ),
        }
      : {}),
    ...(snapshot.directionRequirements
      ? {
          directionRequirements: {
            ...defaultDirectionRequirements,
            ...snapshot.directionRequirements,
          },
        }
      : {}),
    ...(snapshot.directionContent
      ? {
          directionContent: {
            ...defaultDirectionContent,
            ...snapshot.directionContent,
          },
        }
      : {}),
    drafts,
    references: Array.isArray(snapshot.references) ? snapshot.references : [],
    generatedImages: Array.isArray(snapshot.generatedImages)
      ? snapshot.generatedImages
      : [],
    selectedDraftIds: Array.isArray(snapshot.selectedDraftIds)
      ? snapshot.selectedDraftIds.filter((id) => availableDraftIds.has(id))
      : [],
  };
}

export function buildStrictReviewImageRequest(
  direction: Direction,
  prompt: string,
  referenceImages: string[],
): ImageGenerationRequest {
  if (!referenceImages.length) throw new Error("请先选择至少 1 张产品参考图");
  return {
    provider: "openai",
    prompt,
    count: 1,
    aspectRatio:
      direction === "scene"
        ? "landscape"
        : direction === "retail"
          ? "landscape"
        : direction === "model"
          ? "portrait"
          : "square",
    referenceImages,
    strictReferenceValidation: true,
  };
}

export function imageProviderTestError(result: {
  ok: boolean;
  message: string;
}) {
  return result.ok ? "" : `连接检测失败：${result.message}`;
}

export const imageProviderMissingKeyMessage =
  "请填写 API Key。图片生成尚未开始。";

export function imageProviderSetupError(
  hasSavedApiKey: boolean,
  draftApiKey: string,
) {
  return hasSavedApiKey || draftApiKey.trim()
    ? ""
    : imageProviderMissingKeyMessage;
}

export const reviewImageMaxConcurrency = 1;
export const reviewImageProviderTestOperation = "edit" as const;
export const reviewImageInitialConcurrency = 1;
export const reviewImageAdaptiveCeiling = 1;
export const standaloneImageMaxConcurrency = 3;
export const standaloneImageInitialConcurrency = 3;

export interface ReviewImageConcurrencyState {
  limit: number;
  fastSuccesses: number;
}

export function adjustReviewImageConcurrency(
  state: ReviewImageConcurrencyState,
  _outcome: "fast-success" | "success" | "pressure",
): ReviewImageConcurrencyState {
  const limit = Math.min(
    reviewImageAdaptiveCeiling,
    Math.max(1, Math.floor(state.limit) || reviewImageInitialConcurrency),
  );
  return { limit, fastSuccesses: 0 };
}

export function reviewImageWorkerCount(taskCount: number) {
  if (!Number.isFinite(taskCount) || taskCount <= 0) return 0;
  return Math.min(
    Math.floor(taskCount),
    reviewImageAdaptiveCeiling,
    reviewImageMaxConcurrency,
  );
}

export function shouldReviewImageWorkerWait(
  workerIndex: number,
  cursor: number,
  taskCount: number,
  state: ReviewImageConcurrencyState,
  paused: boolean,
) {
  return (
    cursor < taskCount && (paused || workerIndex >= Math.max(1, state.limit))
  );
}

export async function runBoundedImageTasks<T>(
  taskCount: number,
  concurrency: number,
  task: (index: number) => Promise<T>,
  maxConcurrency = standaloneImageMaxConcurrency,
  initialConcurrency = standaloneImageInitialConcurrency,
): Promise<PromiseSettledResult<T>[]> {
  if (!Number.isFinite(taskCount) || taskCount <= 0) return [];
  const count = Math.floor(taskCount);
  const limit = Math.min(
    count,
    Math.max(1, Math.floor(maxConcurrency) || 1),
    Math.max(1, Math.floor(concurrency) || initialConcurrency),
  );
  const results = Array<PromiseSettledResult<T>>(count);
  let cursor = 0;
  const worker = async () => {
    while (cursor < count) {
      const index = cursor++;
      try {
        results[index] = { status: "fulfilled", value: await task(index) };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }
    }
  };
  await Promise.all(Array.from({ length: limit }, () => worker()));
  return results;
}

export function runBoundedReviewImageTasks<T>(
  taskCount: number,
  concurrency: number,
  task: (index: number) => Promise<T>,
) {
  return runBoundedImageTasks(
    taskCount,
    concurrency,
    task,
    reviewImageMaxConcurrency,
    reviewImageInitialConcurrency,
  );
}

export async function requestSingleReviewImage(
  request: ImageGenerationRequest,
  generateImages: (
    input: ImageGenerationRequest,
  ) => Promise<{ images: GeneratedImage[] }>,
) {
  const result = await generateImages(request);
  const image = result.images[0];
  if (!image) throw new Error("接口未返回图片");
  return image;
}

const directions: Array<{
  key: Direction;
  title: string;
  detail: string;
  icon: typeof Camera;
}> = [
  {
    key: "buyer",
    title: "买家秀",
    detail: "生活化手持与开箱记录",
    icon: Camera,
  },
  {
    key: "model",
    title: "AI 模特手持",
    detail: "匹配目标人群的产品展示",
    icon: UserRound,
  },
  {
    key: "effect",
    title: "使用效果图",
    detail: "参考图支持的取用、使用与真实状态",
    icon: Sparkles,
  },
  {
    key: "scene",
    title: "场景拍摄",
    detail: "符合品类的真实使用或收纳场景",
    icon: Image,
  },
  {
    key: "comparison",
    title: "用后对比效果",
    detail: "同一对象使用前后的真实对照",
    icon: Images,
  },
  {
    key: "retail",
    title: "免税店商超特卖",
    detail: "免税店、商超货架与限时特卖陈列",
    icon: Store,
  },
];

const branchItems: Array<{
  key: Branch;
  title: string;
  detail: string;
  icon: typeof Camera;
}> = [
  {
    key: "analysis",
    title: "卖点整理",
    detail: "人群、痛点与真实卖点",
    icon: FileSearch,
  },
  {
    key: "reviews",
    title: "图文评价生成",
    detail: "3–5 图配套写实评语",
    icon: WandSparkles,
  },
  {
    key: "images",
    title: "图片创作任务",
    detail: "六种方向统一管理",
    icon: Images,
  },
  {
    key: "history",
    title: "生成记录",
    detail: "草稿、复制与复用",
    icon: History,
  },
  {
    key: "database",
    title: "素材数据库",
    detail: "原图、评语与下载",
    icon: Database,
  },
];

const providerLabels: Record<ImageProviderId, string> = {
  doubao: "豆包",
  openai: "GPT Image 2",
  custom: "其他平台",
};
const providerDefaults: Record<
  ImageProviderId,
  Omit<ImageProviderInput, "apiKey">
> = {
  doubao: {
    provider: "doubao",
    label: "豆包图片生成",
    baseUrl: "https://ark.cn-beijing.volces.com/api/v3/images/generations",
    model: "doubao-seedream-5-0-260128",
    enabled: true,
  },
  openai: {
    provider: "openai",
    label: "GPT Image 2（Codex）",
    baseUrl: "https://newapi.1to10.cn/v1/images/generations",
    model: "gpt-image-2",
    enabled: true,
  },
  custom: {
    provider: "custom",
    label: "其他兼容平台",
    baseUrl: "https://api.example.com/v1/images/generations",
    model: "image-model",
    enabled: false,
  },
};
const directionPrompts: Record<Direction, string> = {
  buyer: "真实买家实拍与开箱记录，生活化构图，自然光线",
  model: "符合目标人群的模特自然手持商品，主体和包装清晰",
  effect: "只展示参考图能够证实的取用、使用方式和真实可见状态",
  scene: "把商品放在适合品类的真实生活场景中，环境整洁自然",
  comparison: "同一对象、同一部位和同一机位呈现使用前后的真实对照",
  retail: "把商品放在真实免税店或大型商超的限时特卖陈列中，保留卖场购物氛围",
};
const defaultGenerationRequirements =
  "写实手机实拍风格，自然光线和真实生活构图，保留皮肤纹理、手部细节、产品材质、轻微环境杂物与合理阴影。避免过度磨皮、塑料皮肤、完美对称、悬浮物体、夸张景深、影棚广告感和明显AI痕迹。";
const reviewModelProfiles = [
  "约 24 岁，鹅蛋脸、暖白肤色、杏眼、平直眉、小巧鼻头、深棕色中长直发，左眼下有一颗浅色小痣",
  "约 30 岁，偏圆脸、自然小麦肤色、内双眼、自然弯眉、圆润鼻头、黑色锁骨短发，笑时有浅酒窝",
  "约 28 岁，窄长脸、白皙肤色、细长眼、清晰眉峰、窄鼻梁、深棕色低马尾，薄唇",
  "约 35 岁，柔和方脸、健康肤色、圆眼、浓直眉、挺鼻梁、黑色齐肩微卷发，右眉尾有浅痣",
  "约 26 岁，心形脸、中性肤色、杏仁眼、细弯眉、小翘鼻、栗棕色长直发，下唇较饱满",
  "约 32 岁，长鹅蛋脸、暖黄肤色、单眼皮、自然粗眉、直鼻梁、黑色自然卷长发，颧骨柔和清晰",
  "约 29 岁，圆脸、冷白肤色、圆眼、短平眉、小圆鼻、深棕色耳下短发，面颊有轻微雀斑",
  "约 38 岁，偏长脸、自然肤色、内双眼、弧形眉、较长鼻梁、黑色中长微卷发，眼尾有自然细纹",
  "约 23 岁，小圆脸、暖白肤色、下垂眼、柔和直眉、小巧鼻梁、黑色齐肩直发，右侧嘴角有浅痣",
  "约 34 岁，菱形脸、中性肤色、细长双眼皮、上扬眉、窄直鼻、深棕色盘发，颧骨轮廓明显",
  "约 27 岁，短鹅蛋脸、自然肤色、大杏眼、浓弯眉、圆鼻头、栗色肩上卷发，笑时露出轻微虎牙",
  "约 40 岁，长方脸、暖黄肤色、内双眼、平缓眉峰、挺直鼻梁、黑色低盘发，法令纹自然可见",
  "约 25 岁，倒三角脸、冷白肤色、丹凤眼、细平眉、小直鼻、深棕色齐耳短发，左眉上有浅痣",
  "约 31 岁，饱满鹅蛋脸、小麦肤色、圆杏眼、自然浓眉、宽鼻翼、黑色高马尾，笑时双侧酒窝明显",
  "约 36 岁，窄方脸、中性肤色、单眼皮、略上扬眉、长鼻梁、深棕色齐肩直发，嘴唇轮廓清晰",
  "约 22 岁，心形圆脸、暖白肤色、圆眼、短弯眉、小圆鼻、黑色双层次长发，鼻梁有少量雀斑",
  "约 33 岁，长脸、健康小麦肤色、深眼窝双眼皮、浓平眉、挺鼻、黑色短卷发，左侧脸颊有浅痣",
  "约 28 岁，柔和方圆脸、自然肤色、内双杏眼、弯月眉、圆润鼻梁、栗棕色低丸子头，上唇偏薄",
  "约 37 岁，鹅蛋脸、冷白肤色、细长眼、清晰平眉、窄鼻头、黑色齐肩波浪发，右眼下有淡痣",
  "约 26 岁，菱形鹅蛋脸、暖黄肤色、外双眼、略粗弯眉、小翘鼻、深棕色高马尾，面中有自然浅雀斑",
  "约 41 岁，圆润鹅蛋脸、自然肤色、内双杏眼、舒展平眉、圆鼻头、黑色齐颈短发，笑纹自然",
  "约 24 岁，窄鹅蛋脸、冷白肤色、圆杏眼、细直眉、小翘鼻、栗棕色长卷发，左脸颊有浅痣",
  "约 39 岁，柔和长方脸、暖黄肤色、单眼皮、浓弯眉、挺直鼻梁、深棕色齐肩卷发，嘴角微微上扬",
  "约 27 岁，短圆脸、中性肤色、下垂杏眼、自然粗眉、小圆鼻、黑色及肩层次发，面颊饱满",
  "约 33 岁，心形脸、健康小麦肤色、外双眼、清晰眉峰、窄鼻梁、黑色长波浪发，右眉心有淡痣",
  "约 45 岁，方圆脸、自然肤色、细长内双眼、平缓浓眉、宽直鼻梁、深棕色耳下卷发，眼角细纹自然",
  "约 22 岁，长鹅蛋脸、暖白肤色、大圆眼、短直眉、小巧鼻头、黑色长直发，笑时露出小虎牙",
  "约 31 岁，菱形脸、冷白肤色、丹凤眼、上扬细眉、窄鼻头、栗色齐肩直发，颧骨线条清晰",
  "约 36 岁，饱满圆脸、暖黄肤色、双眼皮圆眼、柔和弯眉、圆润鼻翼、黑色低马尾，左侧浅酒窝",
  "约 29 岁，窄长脸、自然小麦肤色、单眼皮、平直粗眉、挺鼻梁、深棕色短直发，下颌线清晰",
  "约 42 岁，鹅蛋方脸、中性肤色、内双眼、自然眉峰、长鼻梁、黑色低盘发，法令纹轻微可见",
  "约 25 岁，心形鹅蛋脸、暖白肤色、杏仁眼、细弯眉、小直鼻、栗棕色双层次中长发，鼻尖有浅痣",
  "约 34 岁，偏圆鹅蛋脸、健康肤色、圆眼、短平眉、宽鼻头、黑色高丸子头，笑时双颊饱满",
  "约 28 岁，柔和方脸、冷白肤色、细长双眼皮、清晰直眉、窄直鼻、深棕色锁骨卷发，上唇轮廓清晰",
  "约 38 岁，长菱形脸、暖黄肤色、深眼窝内双、略粗弯眉、挺鼻、黑色齐肩直发，右颧骨有浅痣",
  "约 23 岁，小鹅蛋脸、中性肤色、下垂圆眼、柔和短眉、小圆鼻、栗色耳下短卷发，脸颊有淡雀斑",
  "约 32 岁，倒三角脸、自然肤色、丹凤双眼皮、浓平眉、小翘鼻、黑色中长低马尾，下唇饱满",
  "约 44 岁，长圆脸、暖黄肤色、单眼皮、舒展弧形眉、圆鼻梁、深棕色齐颈微卷发，眼尾纹自然",
  "约 26 岁，菱形圆脸、冷白肤色、大杏眼、上扬眉峰、窄鼻梁、黑色长卷高马尾，右嘴角有浅痣",
  "约 35 岁，短方脸、健康小麦肤色、内双圆眼、自然粗眉、宽直鼻、栗棕色齐耳直发，轮廓利落",
  "约 30 岁，长心形脸、暖白肤色、细长眼、细平眉、小巧鼻头、深棕色低丸子头，左眉尾有淡痣",
  "约 40 岁，柔和鹅蛋脸、中性肤色、外双杏眼、浓弯眉、挺直鼻梁、黑色肩上波浪发，笑纹柔和",
  "约 27 岁，饱满方圆脸、自然肤色、单眼皮、短直眉、圆鼻头、栗色长直发，鼻梁有稀疏雀斑",
  "约 37 岁，窄鹅蛋脸、暖黄肤色、丹凤内双眼、清晰眉峰、长窄鼻、黑色齐肩层次发，颧骨偏高",
  "约 21 岁，小圆心形脸、冷白肤色、圆杏眼、柔和弯眉、小翘鼻、深棕色双辫长发，笑时有右酒窝",
  "约 43 岁，宽鹅蛋脸、健康肤色、细长单眼皮、自然平眉、宽鼻翼、黑色短卷发，额角细纹自然",
  "约 33 岁，长方鹅蛋脸、暖白肤色、双眼皮圆眼、略粗直眉、挺鼻头、栗棕色中长直发，左眼尾有浅痣",
  "约 28 岁，短菱形脸、中性肤色、内双杏眼、细眉峰、小直鼻、黑色齐颈波浪发，嘴唇偏饱满",
  "约 36 岁，圆润心形脸、自然小麦肤色、下垂眼、浓弧形眉、圆鼻梁、深棕色高马尾，双颊有浅雀斑",
  "约 25 岁，长圆脸、暖黄肤色、外双细长眼、短平眉、窄鼻头、黑色肩下自然卷发，右眉上有淡痣",
] as const;

function reviewModelIdentityHash(value: string) {
  let hash = 2166136261;
  for (const character of value) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return hash;
}

/**
 * Create a stable, group-specific character profile. The profile is persisted
 * on the draft so retries and refreshes keep the same identity instructions.
 */
export function buildReviewGroupModelIdentity(
  groupId: string,
  groupIndex: number,
) {
  const batchOffset =
    reviewModelIdentityHash(groupId) % reviewModelProfiles.length;
  return reviewModelProfiles[
    (batchOffset + groupIndex) % reviewModelProfiles.length
  ]!;
}

export function reviewGroupModelIdentityForGeneration(
  lockGroupModel: boolean,
  modelIdentity?: string,
) {
  return lockGroupModel ? modelIdentity : undefined;
}

const reviewGroupWardrobes = [
  "米白色针织上衣、深蓝色直筒裤、白色低帮鞋；只佩戴一对小银色耳钉",
  "浅灰色圆领卫衣、黑色休闲长裤、白色运动鞋；不佩戴其他首饰",
  "浅蓝色牛仔外套、白色纯色T恤、深色长裤、米色帆布鞋；只戴一块黑色手表",
  "浅棕色开衫、黑色长裙、棕色平底鞋；不更换配饰",
  "深绿色短袖上衣、米色阔腿裤、白色休闲鞋；只佩戴细银色项链",
  "藏蓝色衬衫、浅灰色长裤、黑色便鞋；不佩戴帽子或围巾",
  "雾粉色圆领针织衫、炭灰色直筒裤、米白色乐福鞋；只戴一只细金色手镯",
  "白色亚麻衬衫、浅卡其色阔腿裤、棕色凉鞋；只佩戴小珍珠耳钉",
  "浅紫色卫衣、深灰色束脚裤、灰白色运动鞋；不佩戴首饰",
  "墨绿色衬衫裙、黑色低跟鞋；只佩戴一块棕色皮带手表",
  "奶黄色短袖针织上衣、浅蓝色直筒牛仔裤、白色帆布鞋；不佩戴项链",
  "深灰色圆领T恤、军绿色工装裤、黑白运动鞋；只戴黑色细框眼镜",
  "浅粉色棉质衬衫、米白色半身长裙、裸色平底鞋；只佩戴细金色项链",
  "黑色短袖针织衫、浅灰色西装裤、黑色乐福鞋；只戴银色腕表",
  "天蓝色圆领上衣、白色直筒裤、浅灰色休闲鞋；不佩戴其他配饰",
  "砖红色开衫、米色圆领内搭、深蓝色长裤、棕色平底鞋；只戴小金色耳钉",
  "白色短袖衬衫、藏青色A字长裙、黑色玛丽珍鞋；不佩戴首饰",
  "浅绿色连帽卫衣、米色休闲裤、白绿色运动鞋；只戴白色运动手表",
  "深蓝色牛仔衬衫、浅灰色纯色T恤、黑色直筒裤、白色板鞋；不佩戴项链",
  "焦糖色圆领毛衣、深棕色半身裙、黑色短靴；只佩戴琥珀色耳钉",
  "米白色短袖上衣、橄榄绿色阔腿裤、棕色编织凉鞋；不佩戴手表",
  "浅灰蓝色针织开衫、白色吊带内搭、深色牛仔裤、米色休闲鞋；只戴细银手链",
  "酒红色圆领卫衣、黑色直筒长裤、灰色运动鞋；不佩戴任何首饰",
  "白色立领衬衫、驼色九分裤、深棕色乐福鞋；只佩戴圆形金色耳钉",
  "浅卡其色工装外套、黑色纯色T恤、蓝色牛仔裤、黑色帆布鞋；只戴深色腕表",
  "豆绿色短袖针织衫、米白色直筒长裙、浅棕色平底鞋；只佩戴小银色项链",
  "藏青色圆领毛衣、灰蓝色长裤、白色休闲鞋；不佩戴帽子和首饰",
  "淡黄色棉质连衣裙、米白色低跟鞋；只佩戴一对小珍珠耳环",
  "浅灰色西装外套、白色圆领T恤、黑色直筒裤、灰色乐福鞋；只戴银色手表",
  "珊瑚粉短袖上衣、浅米色阔腿裤、白色低帮鞋；不佩戴其他配饰",
  "墨蓝色短袖衬衫、卡其色直筒裤、棕色便鞋；只佩戴黑色细皮带",
  "白色针织背心、浅蓝色薄款开衫、深灰色长裙、米色平底鞋；只戴细银耳圈",
  "森林绿色卫衣、浅灰色运动长裤、白黑色运动鞋；不佩戴首饰或帽子",
  "浅棕色亚麻衬衫、奶白色直筒裤、棕色凉鞋；只佩戴木色小耳钉",
  "深紫色圆领针织衫、黑色阔腿裤、黑色低跟鞋；只戴一条细银项链",
  "雾蓝色连衣长裙、白色针织薄外套、裸色平底鞋；不佩戴手表",
  "米色圆领卫衣、深绿色休闲裤、灰白色板鞋；只戴黑色运动手环",
  "黑白细条纹长袖上衣、深蓝色牛仔裤、白色帆布鞋；只佩戴小银色耳钉",
  "浅杏色衬衫、烟灰色半身长裙、深棕色乐福鞋；只戴方形棕色腕表",
  "灰绿色短袖上衣、白色直筒裤、浅棕色休闲鞋；不佩戴项链或耳饰",
  "藏蓝色针织开衫、浅灰色圆领内搭、米色长裤、黑色平底鞋；只佩戴珍珠耳钉",
  "白色连帽卫衣、浅蓝色宽松牛仔裤、灰色运动鞋；不佩戴任何首饰",
  "栗红色衬衫、炭黑色直筒裤、深棕色便鞋；只戴一块金属方形手表",
  "淡紫灰色短袖针织衫、白色A字长裙、米色低帮鞋；只佩戴细银手镯",
  "浅蓝色棉质连衣裙、奶白色平底鞋；只戴一对小金色耳圈",
  "深绿色针织背心、白色长袖衬衫、黑色长裤、棕色乐福鞋；不佩戴项链",
  "燕麦色圆领毛衣、浅灰蓝色直筒裤、白色运动鞋；只佩戴浅棕色腕表",
  "黑色牛仔外套、灰色纯色T恤、军绿色长裤、黑色板鞋；只戴银色耳骨夹",
  "浅粉紫色开衫、米白色内搭、深灰色长裙、裸色平底鞋；只佩戴细金色手链",
  "湖蓝色短袖衬衫、白色阔腿裤、浅灰色休闲鞋；不佩戴帽子或围巾",
] as const;

const legacyReviewGroupWardrobes = reviewGroupWardrobes.slice(0, 6);

export const reviewModelTemplates = reviewModelProfiles.map(
  (profile, index) => ({
    id: index + 1,
    profile,
    wardrobe: reviewGroupWardrobes[index]!,
  }),
);

export function buildReviewGroupWardrobe(modelIdentity?: string) {
  const identity = modelIdentity?.trim();
  if (!identity) return "";
  const profileIndex = reviewModelProfiles.indexOf(
    identity as (typeof reviewModelProfiles)[number],
  );
  return reviewGroupWardrobes[
    (profileIndex >= 0 ? profileIndex : reviewModelIdentityHash(identity)) %
      reviewGroupWardrobes.length
  ]!;
}

function buildLegacyReviewGroupWardrobe(modelIdentity: string) {
  const profileIndex = reviewModelProfiles.indexOf(
    modelIdentity as (typeof reviewModelProfiles)[number],
  );
  return legacyReviewGroupWardrobes[
    (profileIndex >= 0 ? profileIndex : reviewModelIdentityHash(modelIdentity)) %
      legacyReviewGroupWardrobes.length
  ]!;
}

export function buildReviewGroupModelPromptRule(
  modelIdentity?: string,
  modelWardrobe?: string,
) {
  const identity = modelIdentity?.trim();
  if (!identity) return "";
  const wardrobe = modelWardrobe?.trim() || buildReviewGroupWardrobe(identity);
  return `【最高优先级：同组锁定同一模特与穿搭】把“${identity}”视为本评价组唯一固定真人模特的选角卡。凡画面出现人物，每一次独立生成都必须复现同一个人的身份，不是重新随机生成外貌相似的人；本组所有图片的脸型、五官比例、年龄、肤色、发型、发色、体型、身高观感、肩宽、四肢粗细和身体比例必须一致。同组固定穿着：${wardrobe}。同组所有图片必须保持完全相同的服装款式、颜色、鞋子和配饰，不得换衣服、增删配饰或改变身体特征。只允许改变动作、表情、机位、拍摄距离和场景；不同评价组必须重新分配人物与穿搭，不得沿用这个人物或这套衣服。纯产品场景可以不出现人物。`;
}

/** Backfill identities and preserve the original six-look mapping for old drafts. */
export function ensureReviewGroupModelIdentities(
  drafts: ReviewDraft[],
  lockGroupModel = true,
) {
  if (!lockGroupModel) return drafts;
  return drafts.map((draft, index) => {
    const savedIdentity = draft.modelIdentity?.trim() || undefined;
    const modelIdentity =
      savedIdentity ??
      buildReviewGroupModelIdentity(draft.batchId ?? draft.id, index);
    const modelWardrobe =
      draft.modelWardrobe?.trim() ||
      (savedIdentity
        ? buildLegacyReviewGroupWardrobe(savedIdentity)
        : buildReviewGroupWardrobe(modelIdentity));
    if (
      draft.modelIdentity === modelIdentity &&
      draft.modelWardrobe === modelWardrobe
    ) {
      return draft;
    }
    return { ...draft, modelIdentity, modelWardrobe };
  });
}
const defaultReviewRequirements =
  "像真人随便聊两句，别像广告。别每条都重复“很好用/质量好/物流快”，换说法。多写具体场景（在家、出差、出去和朋友玩、赶时间）、上手触感、气味、用完的状态，加一两处细节（盖子好不好拧、带出门方不方便、清洗麻不麻烦、用到第几次才发现效果）。语气带点犹豫或吐槽更真实，比如“一开始没抱希望”“一开始有点不相信”，身份模糊点更自然。";
const legacyDirectionRequirements: Record<Direction, string> = {
  buyer:
    "普通用户随手拍摄，构图不过度规整，保留真实生活环境、轻微使用痕迹和手机镜头质感。",
  model:
    "自然生活状态下手持产品，人物不过度精修，保留真实肤质和准确手指结构，产品包装正面清楚。",
  effect:
    "真实展示按压、质地、泡沫或使用过程，液体、皮肤、水滴和接触关系符合物理规律。",
  scene:
    "产品自然放置在真实浴室或生活空间，光线、反射、阴影与环境比例合理，避免商业影棚布景。",
  comparison:
    "同一人物或同一部位保持相同机位、姿态和光线，左右呈现真实克制的使用前后对比。",
  retail:
    "商品真实陈列在免税店或大型商超的货架、端架、堆头或促销台，保留线下限时特卖氛围。",
};
const defaultDirectionRequirements: Record<Direction, string> = {
  buyer:
    "普通用户随手拍摄，构图不过度规整，保留真实生活环境、轻微使用痕迹和手机镜头质感。",
  model:
    "自然生活状态下手持产品，人物不过度精修，保留真实肤质和准确手指结构，产品包装正面清楚。",
  effect:
    "只按参考图明确展示的开合、取用和使用方式呈现过程；若结构不清楚，保持产品完整闭合并展示品类对应的真实使用结果。",
  scene:
    "产品自然放置在符合该品类的真实使用或收纳空间，光线、反射、阴影与环境比例合理，避免商业影棚布景。",
  comparison:
    "在同一张图中左右呈现使用前与使用后的真实对比；两侧保持同一人物或同一使用部位、相同机位、焦段、姿态、构图和光线，产品身份与标签方向一致。用后变化必须自然克制，只体现已确认卖点，不添加夸张功效、医疗结论、对比文字或虚构数值。",
  retail:
    "商品作为免税店或大型商超限时特卖的主推商品，真实摆放在货架、端架、堆头或促销台；保留卖场灯光、购物通道、货架层次和适量顾客背景。促销牌只表达通用折扣氛围，不生成可读价格、条码、店名、品牌授权、免税资质或官方背书，避免展会布景和影棚柜台感。",
};
export const physicalRealityRequirements =
  "物理真实性和叙事逻辑为最高优先级：只能根据参考图中的包装形态、可见容量标识、手部和周边物体推断真实尺寸，不得默认套用500ml泵瓶或任何其他固定品类的比例。产品与手掌、面部和环境物体比例必须合理，不得异常放大或缩小。每个可见人物只能有一只左手和一只右手；从肩膀到手腕逐一检查两条手臂的连接，绝不能复制第三只手、第三条手臂或同侧的两只手。手掌、手腕和每根手指必须结构完整、关节方向自然、数量正确，握持处有合理遮挡和受力，不得出现粘连、增生、穿模、断指或扭曲。若无法可靠生成手部，改为把产品自然放在符合品类的位置拍摄，不要勉强生成错误手势。只允许一个明确的画面意图：如果人物手持产品且脸部可见，人物必须正面自然看向镜头，只做产品展示，不得同时照顾、触碰或给旁人涂抹；如果人物正在看着孩子或其他使用对象并进行护理，产品必须正向放在桌面或使用位置，不能同时拿在手里。人物五官、四肢、家具、门框、台面和包装轮廓均不得弯曲变形。生成前先检查每个人的左右手数量、视线目标、动作目的，以及产品身份、结构、比例、接触关系、透视和阴影是否符合真实手机照片。";
export const directionRealityRequirements: Record<Direction, string> = {
  buyer:
    "买家随手拍只保留一个明确且合理的拍摄动作；手持产品时避免镜面自拍，画面中不要再出现手机或来源不明的额外手臂。",
  model:
    "这是手持展示场景：人物自然单手握住产品正面，脸部朝向镜头并与镜头有自然眼神交流；另一只手放松，不得同时照顾、涂抹或触碰他人。产品与手掌、面部比例真实，手指被瓶身遮挡的关系必须合理。",
  effect:
    "这是使用护理场景：人物视线自然落在正在护理的孩子、皮肤或使用部位，不看镜头；产品保持正向并放在桌面或使用位置，不拿在手里。护理者的一只手完成单一涂抹动作，另一只手最多只用于自然承托；每个人最多两只手，产品、内容物和接触关系必须符合真实物理规律。",
  scene:
    "以产品自然摆放为主，减少不必要的人手；承托表面、反光材质、包装倒影和环境透视必须一致。",
  comparison:
    "这是同一对象的使用前后对比场景：左右两侧必须是同一人物或同一部位，身份、姿态、机位、焦段、距离和光线保持一致；仅呈现真实、克制、可见的用后差异，不得更换人物、改变骨相或肤色，不得夸张修图、添加医疗化结果、对比文字或数值。",
  retail:
    "这是线下零售特卖场景：产品必须由真实货架、端架或促销台稳定承托，尺寸、透视、接触阴影和卖场灯光一致；包装正面清楚且保持参考图身份。背景货品可适度虚化，不得出现扭曲复制品、悬浮陈列、虚构可读价格、条码、店名、品牌授权、免税资质或官方背书。",
};
const defaultDirectionContent: DirectionContent = Object.fromEntries(
  directions.map((item) => [
    item.key,
    { title: item.title, detail: item.detail },
  ]),
) as DirectionContent;
const legacyDirectionDetails: Record<Direction, string> = {
  buyer: "生活化手持与开箱记录",
  model: "匹配目标人群的产品展示",
  effect: "质地、过程与可见使用状态",
  scene: "浴室、梳妆台或户外等场景",
  comparison: "同一对象使用前后的真实对照",
  retail: "免税店、商超货架与限时特卖陈列",
};

function readStorage<T>(key: string, fallback: T): T {
  try {
    return JSON.parse(localStorage.getItem(key) ?? "null") ?? fallback;
  } catch {
    return fallback;
  }
}

const historyStorageKey = "jianze-review-history-v2";
const workspaceStorageKey = "jianze-review-workspace-v2";

function openHistoryDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open("jianze-review-studio", 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains("records"))
        request.result.createObjectStore("records", { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function loadHistoryRecords() {
  if (typeof indexedDB === "undefined")
    return readStorage<HistoryRecord[]>("jianze-review-history-v1", []);
  const database = await openHistoryDatabase();
  return new Promise<HistoryRecord[]>((resolve, reject) => {
    const request = database
      .transaction("records", "readonly")
      .objectStore("records")
      .get(historyStorageKey);
    request.onsuccess = () =>
      resolve(
        (request.result?.value as HistoryRecord[] | undefined) ??
          readStorage<HistoryRecord[]>("jianze-review-history-v1", []),
      );
    request.onerror = () => reject(request.error);
  }).finally(() => database.close());
}

async function persistHistoryRecords(records: HistoryRecord[]) {
  if (typeof indexedDB === "undefined") {
    localStorage.setItem("jianze-review-history-v1", JSON.stringify(records));
    return;
  }
  const database = await openHistoryDatabase();
  await new Promise<void>((resolve, reject) => {
    const request = database
      .transaction("records", "readwrite")
      .objectStore("records")
      .put({ id: historyStorageKey, value: records });
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  }).finally(() => database.close());
}

async function loadReviewWorkspace() {
  const fallback = restoreReviewWorkspaceForRefresh(
    readStorage<Partial<ReviewWorkspaceSnapshot>>(
      "jianze-review-workspace-v1",
      {},
    ),
  );
  if (typeof indexedDB === "undefined") return fallback;
  try {
    const database = await openHistoryDatabase();
    return await new Promise<Partial<ReviewWorkspaceSnapshot>>(
      (resolve, reject) => {
        const request = database
          .transaction("records", "readonly")
          .objectStore("records")
          .get(workspaceStorageKey);
        request.onsuccess = () =>
          resolve(
            restoreReviewWorkspaceForRefresh(
              (request.result?.value as
                Partial<ReviewWorkspaceSnapshot> | undefined) ?? fallback,
            ),
          );
        request.onerror = () => reject(request.error);
      },
    ).finally(() => database.close());
  } catch {
    return fallback;
  }
}

async function persistReviewWorkspace(snapshot: ReviewWorkspaceSnapshot) {
  if (typeof indexedDB === "undefined") {
    localStorage.setItem(
      "jianze-review-workspace-v1",
      JSON.stringify(snapshot),
    );
    return;
  }
  const database = await openHistoryDatabase();
  await new Promise<void>((resolve, reject) => {
    const request = database
      .transaction("records", "readwrite")
      .objectStore("records")
      .put({ id: workspaceStorageKey, value: snapshot });
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  }).finally(() => database.close());
}

function safeFileName(value: string) {
  return (
    value
      .trim()
      .replace(/[\\/:*?"<>|\s]+/g, "-")
      .slice(0, 80) || "评价素材"
  );
}

export interface ReviewArchiveGroup {
  id: string;
  productName: string;
  reviewText: string;
  directions: string[];
  savedAt?: string;
  images: Array<{ id: string; url: string }>;
}

export interface ReviewArchiveAsset {
  data: ArrayBuffer | Uint8Array;
  extension?: string;
}

export type ReviewArchiveAssetLoader = (
  url: string,
) => Promise<ReviewArchiveAsset>;

const reviewArchiveMaxConcurrency = 6;

function archiveImageExtension(contentType: string | null, url: string) {
  const mime = (contentType ?? url.match(/^data:([^;,]+)/)?.[1] ?? "")
    .toLowerCase()
    .split(";")[0];
  if (mime === "image/jpeg" || mime === "image/jpg") return "jpg";
  if (mime === "image/webp") return "webp";
  if (mime === "image/gif") return "gif";
  if (mime === "image/avif") return "avif";
  if (mime === "image/png") return "png";
  const pathExtension = url
    .split(/[?#]/)[0]
    ?.match(/\.([a-z0-9]{2,5})$/i)?.[1]
    ?.toLowerCase();
  return ["jpg", "jpeg", "png", "webp", "gif", "avif"].includes(
    pathExtension ?? "",
  )
    ? pathExtension === "jpeg"
      ? "jpg"
      : pathExtension!
    : "png";
}

async function loadReviewArchiveAsset(
  url: string,
): Promise<ReviewArchiveAsset> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`原图读取失败（${response.status}）`);
  return {
    data: await response.arrayBuffer(),
    extension: archiveImageExtension(response.headers.get("content-type"), url),
  };
}

function reviewArchiveCopy(group: ReviewArchiveGroup) {
  return group.reviewText;
}

export function normalizeReviewCountInput(value: string, fallback: number) {
  const parsed = value.trim() === "" ? fallback : Number(value);
  const count = Number.isFinite(parsed) ? parsed : fallback;
  return Math.min(20, Math.max(1, Math.round(count)));
}

function normalizedReviewForSimilarity(value: string) {
  return value
    .toLowerCase()
    .replace(/[\s\u3000，。！？；：、“”‘’（）【】《》,.!?;:'"()[\]{}、]/g, "")
    .trim();
}

function reviewCharacterNgrams(value: string, size = 3) {
  const grams = new Set<string>();
  for (let index = 0; index <= value.length - size; index += 1) {
    grams.add(value.slice(index, index + size));
  }
  return grams;
}

/**
 * Detects near-duplicate buyer reviews, not just byte-for-byte duplicates.
 * The short prefix/suffix checks catch repeated templates; 3-gram overlap
 * catches the same sentence skeleton with a few words swapped.
 */
export function reviewTextsTooSimilar(first: string, second: string) {
  const left = normalizedReviewForSimilarity(first);
  const right = normalizedReviewForSimilarity(second);
  if (!left || !right) return false;
  if (left === right) return true;
  if (Math.min(left.length, right.length) < 12) return false;
  if (left.slice(0, 8) === right.slice(0, 8)) return true;
  if (left.slice(-8) === right.slice(-8)) return true;

  const leftGrams = reviewCharacterNgrams(left);
  const rightGrams = reviewCharacterNgrams(right);
  const intersection = [...leftGrams].filter((gram) => rightGrams.has(gram)).length;
  const union = new Set([...leftGrams, ...rightGrams]).size;
  return union > 0 && intersection / union >= 0.5;
}

export function selectDiverseReviewTexts(
  candidates: string[],
  previousReviews: string[],
  count: number,
) {
  const selected: string[] = [];
  for (const candidate of candidates) {
    const text = candidate.trim();
    if (!text) continue;
    if (
      [...previousReviews, ...selected].some((previous) =>
        reviewTextsTooSimilar(text, previous),
      )
    ) {
      continue;
    }
    selected.push(text);
    if (selected.length >= count) break;
  }
  return selected;
}

export function reviewArchiveFileName(groups: ReviewArchiveGroup[]) {
  if (groups.length === 1)
    return `${safeFileName(groups[0]!.productName)}-图文原图.zip`;
  return `评价素材-${groups.length}组.zip`;
}

function reviewArchiveRootFolderName(groups: ReviewArchiveGroup[]) {
  return reviewArchiveFileName(groups).replace(/\.zip$/i, "");
}

export async function createReviewArchive(
  groups: ReviewArchiveGroup[],
  loadAsset: ReviewArchiveAssetLoader = loadReviewArchiveAsset,
) {
  if (!groups.length) throw new Error("没有可打包的评价素材");
  if (groups.some((group) => !group.images.length))
    throw new Error("所选评价中存在没有原图的内容，无法打包");

  const zip = new JSZip();
  const archiveRoot = zip.folder(reviewArchiveRootFolderName(groups))!;
  const imageJobs: Array<{
    folder: JSZip;
    image: ReviewArchiveGroup["images"][number];
    imageIndex: number;
    direction: string;
  }> = [];

  groups.forEach((group, groupIndex) => {
    const folder = archiveRoot.folder(
      `${String(groupIndex + 1).padStart(2, "0")}-${safeFileName(group.productName)}`,
    )!;
    folder.file("评价文案.txt", `\uFEFF${reviewArchiveCopy(group)}`);
    group.images.forEach((image, imageIndex) => {
      imageJobs.push({
        folder,
        image,
        imageIndex,
        direction: group.directions[imageIndex] ?? "原图",
      });
    });
  });

  let nextJob = 0;
  const workerCount = Math.min(reviewArchiveMaxConcurrency, imageJobs.length);
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (nextJob < imageJobs.length) {
        const job = imageJobs[nextJob++]!;
        const asset = await loadAsset(job.image.url);
        const extension = archiveImageExtension(
          asset.extension ? `image/${asset.extension}` : null,
          job.image.url,
        );
        job.folder.file(
          `${String(job.imageIndex + 1).padStart(2, "0")}-${safeFileName(job.direction)}.${extension}`,
          asset.data,
        );
      }
    }),
  );

  return zip.generateAsync({
    type: "uint8array",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
}

function triggerReviewArchiveDownload(archive: Uint8Array, fileName: string) {
  const data = archive.buffer.slice(
    archive.byteOffset,
    archive.byteOffset + archive.byteLength,
  ) as ArrayBuffer;
  const objectUrl = URL.createObjectURL(
    new Blob([data], { type: "application/zip" }),
  );
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

export function allocateDirections(selected: Direction[], count: number) {
  if (!selected.length || count < 1) return [];
  return Array.from(
    { length: count },
    (_, index) => selected[index % selected.length]!,
  );
}

export function filterReviewLibrary(
  items: ReviewLibraryItem[],
  keyword: string,
) {
  const term = keyword.trim().toLocaleLowerCase("zh-CN");
  if (!term) return items;
  return items.filter((item) =>
    [
      item.productName,
      item.reviewText,
      item.directions.join(" "),
      item.savedAt,
      new Date(item.savedAt).toLocaleString("zh-CN"),
    ]
      .join(" ")
      .toLocaleLowerCase("zh-CN")
      .includes(term),
  );
}

export function updateReviewLibrarySelection(
  currentIds: string[],
  targetIds: string[],
  selected: boolean,
) {
  const next = new Set(currentIds);
  targetIds.forEach((id) => (selected ? next.add(id) : next.delete(id)));
  return [...next];
}

export function selectedReviewLibraryItems(
  items: ReviewLibraryItem[],
  selectedIds: string[],
) {
  const selected = new Set(selectedIds);
  return items.filter((item) => selected.has(item.id));
}

export function productStructureRequirement(product: string) {
  if (!/(儿童防晒|socus|children'?s sunscreen)/i.test(product.trim()))
    return "";
  return "本商品包装结构和标签方向为不可更改的硬性约束：瓶身是白色扁平圆角造型，顶部是黄色圆顶盖。最终画面必须让正面标签保持人眼可直接阅读的正向姿态：SOCUS 标志在最上方，黄色太阳与 SPF 50 在中间，‘儿童防晒乳’和 ‘Children's Sunscreen’ 在下方；所有文字必须从左到右正向、不得镜像、倒置、反写或旋转九十度。以这个标签阅读方向为准，黄色盖和出液口始终在标签上方，瓶底始终在标签下方且完整封闭。禁止为了挤出乳液而把整个瓶身倒过来，使用效果图应让产品保持正向直立或正向手持，并在皮肤上展示已经挤出或已经涂抹的乳液。未使用时黄色盖完整扣在瓶身顶部，白色出液嘴不可见；开盖展示时黄色盖必须已经取下，只露出瓶身顶部中央唯一一个白色锥形尖嘴，不得同时表现正在向下挤压出液。盖合状态和开盖状态只能选择一种，绝不能在黄色盖仍扣在顶部时又从瓶底、侧面或其他位置生成尖嘴、泵头或开口，也不能上下各生成一个出口。若无法准确还原开盖结构，必须改为黄色盖完整盖合、产品正向直立的静态展示。";
}

export function referenceStructureRequirement(hasReferenceImages: boolean) {
  if (!hasReferenceImages) return "";
  return "产品准确性高于场景、动作、构图和美观，是本次生成的第一优先级。生成前必须逐张检查全部产品参考图并建立不可更改的结构清单：确认正面与背面、文字正常阅读时的上下方向、外轮廓和长宽比例、材质与颜色、标签版式，以及盖子、泵头、喷头、出口、拉链、提手、配件等部件的准确数量、形状、相对位置和连接关系。全部参考图默认属于同一款同一 SKU 产品的不同视角和细节，必须综合使用全部已选参考图，不得任选一张替代其他参考图，不得把不同视角误认为不同包装版本，也不得混合拼接出参考图中不存在的产品。每张输出都必须保持同一个产品身份。参考图是唯一产品事实来源：不得翻转上下或正反面，不得更换包装类型，不得移动、增删、复制、合并或凭空发明任何部件，不得生成参考图中不存在的第二个开口、出口、盖子、把手或配件。产品标签必须作为不可重绘的原始表面图案严格保留，复制参考图中的文字内容、字形方向、行序、标志、色块和相对位置；最终照片里的可见标签必须能按正常阅读方向直接阅读，不得镜像、反写、上下颠倒或旋转九十度。即使是前置自拍镜头也不得镜像产品。若某个动作需要把产品倒置而导致标签倒置，必须放弃该动作并改为产品正向静态展示。只允许展示参考图能够明确证实的开启、拆卸、按压、挤压、倾倒和出液方式；若开合细节、出口位置或使用机制看不清，必须保持产品直立、完整闭合和静态展示，绝不猜测内部结构或强行生成使用动作。若手部遮挡会导致结构错误，应把产品完整放在符合品类的真实位置。输出前再次对照全部已选参考图检查标签可读方向、文字行序、产品身份、朝向、轮廓、部件数量和部件位置；任何要求发生冲突时，以产品和标签正确为准。";
}

function readDirectionRequirements() {
  const saved = readStorage<Partial<Record<Direction, string>>>(
    "jianze-review-direction-requirements-v1",
    {},
  );
  return Object.fromEntries(
    directions.map(({ key }) => [
      key,
      !saved[key] || saved[key] === legacyDirectionRequirements[key]
        ? defaultDirectionRequirements[key]
        : saved[key],
    ]),
  ) as Record<Direction, string>;
}

function readDirectionContent() {
  const saved = readStorage<Partial<DirectionContent>>(
    "jianze-review-direction-content-v1",
    {},
  );
  return Object.fromEntries(
    directions.map(({ key }) => {
      const current = saved[key];
      return [
        key,
        {
          title: current?.title || defaultDirectionContent[key].title,
          detail:
            !current?.detail || current.detail === legacyDirectionDetails[key]
              ? defaultDirectionContent[key].detail
              : current.detail,
        },
      ];
    }),
  ) as DirectionContent;
}

const reviewExperienceCue =
  /温和|清洁|洗后|用后|肤感|质地|泡沫|香味|留香|清爽|柔嫩|顺滑|滋润|保湿|修护|吸收|紧绷|刺激|轻薄|粘腻|黏腻|假白|搓泥|持久|显色|遮盖|控油|防晒|补涂|便携|携带|方便|顺手|容量|包装|小巧|耐用/;

function cleanReviewSellingPoint(value: string) {
  return value
    .replace(
      /^\s*(?:核心)?(?:产品)?(?:真实)?卖点(?:包括|包含)?\s*[:：]?\s*/,
      "",
    )
    .replace(/^\s*(?:[-*•·]|\d+[.、)])\s*/, "")
    .replace(/[。！？；;，,、]+$/, "")
    .trim();
}

function experienceFromSellingPoint(value: string) {
  const point = cleanReviewSellingPoint(value);
  const clauses = point
    .split(/[，,、]/)
    .map(cleanReviewSellingPoint)
    .filter(Boolean);
  const experientialClauses = clauses.filter((clause) =>
    reviewExperienceCue.test(clause),
  );
  if (
    experientialClauses.length &&
    (point.length > 42 || /^(?:含|采用|添加|精选|富含|蕴含)/.test(point))
  ) {
    return experientialClauses.at(-1)!;
  }
  return point;
}

export function reviewSellingPoints(benefits: string) {
  const normalized = benefits.replace(/\r/g, "\n").trim();
  if (!normalized) return [];
  const sections = normalized
    .split(/[；;\n]+/)
    .map(cleanReviewSellingPoint)
    .filter(Boolean);
  const values =
    sections.length > 1
      ? sections.map(experienceFromSellingPoint)
      : /^(?:含|采用|添加|精选|富含|蕴含)/.test(sections[0] ?? "")
        ? [experienceFromSellingPoint(sections[0] ?? "")]
        : (sections[0] ?? "")
            .split(/[，,、]+/)
            .map(cleanReviewSellingPoint)
            .filter(Boolean);
  return [...new Set(values.filter(Boolean))];
}

const fictionalCelebrityUsage: Record<ReviewEndorsementCountry, string[]> = {
  英国: [
    "之前刷到一位英国电视剧演员的后台洗护记录，洗手台上放着这个牌子，我才留意到它",
    "看一位英国巡演歌手整理随身洗护包时，里面有这个牌子，我才记住它",
  ],
  日本: [
    "之前看一位日本电视剧演员分享收工后的清洁步骤，镜头里有这个牌子，我才注意到它",
    "刷到一位日本巡演歌手整理旅行洗护包时，里面放着这个牌子，我才记住它",
  ],
  韩国: [
    "之前看一位韩国电视剧演员分享化妆间清洁步骤，洗手台上有这个牌子，我才注意到它",
    "刷到一位韩国巡演歌手整理随身洗护包时，里面放着这个牌子，我才记住它",
  ],
};

const fictionalEmbassyUsage: Record<ReviewEndorsementCountry, string[]> = {
  英国: [
    "我表姐在英国使馆相关机构做行政，她说办公室洗手台一直有人放这个牌子，我才注意到它",
    "朋友在英国使馆相关机构工作，出差收拾洗护包时给我看过这个牌子，我才记住它",
  ],
  日本: [
    "我同学在日本使馆相关机构做接待，她说工作人员的旅行洗护包里经常能看到这个牌子，我才注意到它",
    "朋友在日本使馆相关机构工作，出差前收拾洗护包时给我看过这个牌子，我才记住它",
  ],
  韩国: [
    "我朋友在韩国使馆相关机构做行政，她说办公室洗手台一直有人放这个牌子，我才注意到它",
    "同事在韩国使馆相关机构工作，她出差收拾洗护包时给我看过这个牌子，我才记住它",
  ],
};

const reviewEndorsementDisclosure =
  "【内部模拟｜虚构设定｜禁止直接发布】";

export function reviewEndorsementLead(
  settings: Partial<ReviewEndorsementSettings> | null | undefined,
  index = 0,
) {
  const normalized = restoreReviewEndorsementSettings(settings);
  if (!normalized.mentionEmbassy && !normalized.mentionCelebrity) return "";
  const types = [
    ...(normalized.mentionEmbassy ? (["embassy"] as const) : []),
    ...(normalized.mentionCelebrity ? (["celebrity"] as const) : []),
  ];
  const type = types[index % types.length]!;
  const options =
    type === "embassy"
      ? fictionalEmbassyUsage[normalized.country]
      : fictionalCelebrityUsage[normalized.country];
  return options[Math.floor(index / types.length) % options.length]!;
}

function reviewEndorsementIndexes(
  count: number,
  settings: ReviewEndorsementSettings,
) {
  const enabledTypeCount =
    Number(settings.mentionEmbassy) + Number(settings.mentionCelebrity);
  if (!enabledTypeCount || count <= 0) return new Map<number, number>();
  const endorsementCount = Math.min(
    count,
    Math.max(enabledTypeCount, Math.round(count / 3)),
  );
  return new Map(
    Array.from({ length: endorsementCount }, (_, sequenceIndex) => [
      Math.min(
        count - 1,
        Math.floor(((sequenceIndex + 0.5) * count) / endorsementCount),
      ),
      sequenceIndex,
    ]),
  );
}

export function naturalReviews(
  product: string,
  audience: string,
  benefits: string,
  count: number,
  competitorThemes: string[] = [],
  previousReviews: string[] = [],
  requirements = "",
  brandName = "",
  endorsementSettings: ReviewEndorsementSettings =
    defaultReviewEndorsementSettings,
) {
  const points = reviewSellingPoints(benefits);
  const themeExperiences: Record<string, string> = {
    温和肤感: "使用感比较舒服，没有明显负担",
    清洁效果: "实际效果比较清爽，日常使用够用",
    香味留香: "气味自然，不会显得过分浓重",
    泡沫与质地: "可见质地和实际使用状态比较自然",
    包装与容量: "包装和容量设计适合日常使用",
    性价比: "结合容量和使用频率，整体比较实用",
    使用便利: "平时使用顺手，不需要复杂步骤",
    物流与到货: "到货包装完整，开箱状态正常",
  };
  const referencePoints = competitorThemes.flatMap((theme) =>
    themeExperiences[theme] ? [themeExperiences[theme]!] : [],
  );
  const safePoints = points.length
    ? points
    : referencePoints.length
      ? referencePoints
      : ["使用过程比较顺手", "整体感受符合预期", "日常使用没有负担"];
  const baseProductLabel = product.trim() || "这款产品";
  const normalizedBrandName = brandName.trim();
  const contexts = [
    "拆开后按平时习惯试了一下",
    "用了几次才想起来写",
    "放在平时会用到的位置有一阵子了",
    "收到后没马上评价，先按日常方式用了几次",
    "前几天需要的时候试了一次",
    "这两天连续用了几次",
    "周末在家慢慢试了一遍",
    "刚开始还有点担心，后来实际用下来还行",
    "早晚各用了一次，再回来补这条评价",
    "趁着这段时间用得比较规律，记录一下感受",
    "把它放进日常流程里用了一阵子",
    "没有刻意测试，按自己平常的方式用了几天",
    "早上出门前和晚上回家后都顺手用了用",
    "换掉手边旧的那款后，连续体验了几天",
    "这几天正好用到了几次，感受比较完整",
    "从第一次使用到现在，已经有一段时间了",
    "平时想到就用，没有专门为了评价去测试",
    "跟之前的使用习惯对比了一下",
    "在家里和出门时各用过几次",
    "最近用得比较频繁，所以回来补充感受",
    "试着按说明正常用了一轮",
    "把它用进每天的洗护步骤里之后",
    "刚好赶上出差，带着用了几天",
    "这段时间一直放在手边，想到就会用",
    "用了几次之后，觉得可以说说真实感受",
    "没有期待特别夸张的效果，主要看日常是否顺手",
    "对照自己的需求体验了一下",
    "先用了几天，再回来写这条评价",
  ];
  const observations = [
    "最明显的就是",
    "我觉得比较舒服的是",
    "实际用下来，",
    "我自己比较在意的是",
    "用完之后，",
    "说实话，",
    "对我来说，",
    "一开始担心会不太好用，结果",
  ];
  const endings = [
    "反正我自己用着没啥负担。",
    "用下来比之前同类产品顺手。",
    "这点我还挺满意的。",
    "我会继续按平时习惯用。",
    "没有特别惊艳，但确实方便。",
    "先用着，后面有变化再补充。",
    "这次买得不算踩雷。",
    "对我来说够用了。",
  ];
  const detailNotes = [
    "这点是多用了几次才确定的。",
    "我平时怎么用就怎么记，没特意摆拍。",
    "这个小细节对我来说还挺重要。",
    "我更在意这种每天都能感受到的变化。",
    "不是一下子很夸张的效果，但用着比较顺。",
    "这部分是我自己用下来觉得比较实在的地方。",
  ];
  // Audience is used only as internal generation context; never expose the
  // targeting label verbatim in a buyer-style review.
  const audienceNotes = ["", "", ""];
  const concise = /简短|短句|精炼/.test(requirements);
  const detailFocused = /细节|过程|场景|具体/.test(requirements);
  const endorsementIndexes = reviewEndorsementIndexes(
    count,
    restoreReviewEndorsementSettings(endorsementSettings),
  );
  const used = new Set(previousReviews);
  const similarToUsed = (candidate: string) =>
    [...used].some((review) => reviewTextsTooSimilar(candidate, review));
  const pointUsage = new Map(
    safePoints.map((point) => [
      point,
      previousReviews.filter((review) => review.includes(point)).length,
    ]),
  );
  return Array.from({ length: count }, (_, index) => {
    const productLabel =
      shouldMentionReviewBrand(index, count, normalizedBrandName) &&
      !baseProductLabel.includes(normalizedBrandName)
        ? `${normalizedBrandName}这款${baseProductLabel}`
        : baseProductLabel;
    const leastUsed = Math.min(
      ...safePoints.map((point) => pointUsage.get(point) ?? 0),
    );
    const availablePoints = safePoints.filter(
      (point) => (pointUsage.get(point) ?? 0) === leastUsed,
    );
    const point = availablePoints[index % availablePoints.length]!;
    pointUsage.set(point, (pointUsage.get(point) ?? 0) + 1);
    let candidate = "";
    for (let attempt = 0; attempt < 80; attempt += 1) {
      const context = contexts[Math.floor(Math.random() * contexts.length)]!;
      const observation =
        observations[Math.floor(Math.random() * observations.length)]!;
      const ending = endings[Math.floor(Math.random() * endings.length)]!;
      const audienceNote =
        audienceNotes[Math.floor(Math.random() * audienceNotes.length)]!;
      const detail =
        detailFocused && Math.random() > 0.45
          ? detailNotes[Math.floor(Math.random() * detailNotes.length)]!
          : "";
      const pointText = point.replace(/[。！？]+$/, "");
      // Rotate through more sentence shapes so a batch does not read like
      // the same review with only a few words swapped.
      // 30 sentence skeletons × 30 usage contexts = 900+ combinations.
      const structure = index % 30;
      if (concise) {
        candidate =
          structure % 3 === 0
            ? `${context}，${productLabel}${observation}${pointText}。${audienceNote || ending.split("，")[0]}${detail}`
            : structure % 3 === 1
              ? `说实话，${productLabel}用了几次，${pointText}。${audienceNote || ending.split("，")[0]}${detail}`
              : `${productLabel}到手后试了几次，${pointText}，${audienceNote || ending.split("，")[0]}${detail}`;
      } else if (structure === 0) {
        candidate = `${context}，${productLabel}${observation}${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 1) {
        candidate = `说实话，${productLabel}我用了几次，${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 2) {
        candidate = `先说使用感：${pointText}。${context}，${productLabel}${observation}${audienceNote}${detail}${ending}`;
      } else if (structure === 3) {
        candidate = `${productLabel}到手后我没有马上评价，${context}。${observation}${pointText}，${audienceNote}${detail}${ending}`;
      } else if (structure === 4) {
        candidate = `我原本最在意${pointText}，${context}，${productLabel}实际用下来${audienceNote}${detail}${ending}`;
      } else if (structure === 5) {
        candidate = `${context}。${productLabel}给我的感觉是${pointText}，${audienceNote}${detail}${ending}`;
      } else if (structure === 6) {
        candidate = `${productLabel}用了几次再来评价：${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 7) {
        candidate = `简单记录一下，${context}，${productLabel}${pointText}，${audienceNote}${detail}${ending}`;
      } else if (structure === 8) {
        candidate = `${observation}${pointText}。${productLabel}${context}，${audienceNote}${detail}${ending}`;
      } else if (structure === 9) {
        candidate = `这次主要想说${pointText}：${context}，${productLabel}${audienceNote}${detail}${ending}`;
      } else if (structure === 10) {
        candidate = `${productLabel}的这次体验，我会这样概括：${pointText}。${context}，${audienceNote}${detail}${ending}`;
      } else if (structure === 11) {
        candidate = `${context}，先给个实际感受：${pointText}。${productLabel}${audienceNote}${detail}${ending}`;
      } else if (structure === 12) {
        candidate = `用下来我最想记录的是${pointText}。${context}，${productLabel}${audienceNote}${detail}${ending}`;
      } else if (structure === 13) {
        candidate = `${productLabel}这次没有让我失望，${context}，${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 14) {
        candidate = `如果只说一个重点，那就是${pointText}。${productLabel}${context}，${audienceNote}${detail}${ending}`;
      } else if (structure === 15) {
        candidate = `${context}，我对${productLabel}的评价是：${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 16) {
        candidate = `这次买来主要是想解决日常需求，${productLabel}${context}${observation}${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 17) {
        candidate = `${productLabel}实际使用记录：${context}，${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 18) {
        candidate = `先不说别的，${productLabel}${context}${observation}${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 19) {
        candidate = `${context}，试过${productLabel}之后，感觉${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 20) {
        candidate = `我把${productLabel}当作日常使用，${context}，结果是${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 21) {
        candidate = `这条评价写给和我一样在意${pointText}的人。${context}，${productLabel}${audienceNote}${detail}${ending}`;
      } else if (structure === 22) {
        candidate = `${productLabel}从收到到现在的感受：${context}，${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 23) {
        candidate = `我没有马上下结论，${context}之后，${productLabel}${observation}${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 24) {
        candidate = `就日常体验来说，${productLabel}${context}${observation}${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 25) {
        candidate = `${context}，${productLabel}让我比较确定的一点是：${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 26) {
        candidate = `用了这段时间，我会把${productLabel}归到${pointText}这一类。${audienceNote}${detail}${ending}`;
      } else if (structure === 27) {
        candidate = `${productLabel}的使用过程没有太多门槛，${context}${observation}${pointText}。${audienceNote}${detail}${ending}`;
      } else if (structure === 28) {
        candidate = `从我的实际情况看，${pointText}。${context}，${productLabel}${audienceNote}${detail}${ending}`;
      } else {
        candidate = `${context}后回头看，${productLabel}${observation}${pointText}，这就是我愿意留下评价的原因。${audienceNote}${detail}${ending}`;
      }
      if (!used.has(candidate) && !similarToUsed(candidate)) break;
    }
    used.add(candidate);
    const endorsementSequence = endorsementIndexes.get(index);
    if (endorsementSequence === undefined) return candidate;
    const endorsement = reviewEndorsementLead(
      endorsementSettings,
      previousReviews.length + endorsementSequence,
    );
    return `${endorsement}。${candidate}\n${reviewEndorsementDisclosure}`;
  });
}

export function shouldMentionReviewBrand(
  index: number,
  count: number,
  brandName: string,
) {
  const normalizedCount = Number.isFinite(count)
    ? Math.max(0, Math.floor(count))
    : 0;
  if (
    !brandName.trim() ||
    normalizedCount === 0 ||
    index < 0 ||
    index >= normalizedCount
  ) {
    return false;
  }
  const brandedReviewCount = Math.max(1, Math.round(normalizedCount / 3));
  return Array.from({ length: brandedReviewCount }, (_, position) =>
    Math.floor((position * normalizedCount) / brandedReviewCount),
  ).includes(index);
}

export function ReviewStudioPage({
  canManageProvider = true,
}: {
  canManageProvider?: boolean;
}) {
  const savedWorkspace = useMemo(
    () =>
      restoreReviewWorkspaceForRefresh(
        readStorage<Partial<ReviewWorkspaceSnapshot>>(
          "jianze-review-workspace-v1",
          {},
        ),
      ),
    [],
  );
  const queryClient = useQueryClient();
  const providersQuery = useQuery({
    queryKey: ["image-providers"],
    queryFn: dashboardApi.imageProviders,
  });
  const [branch, setBranch] = useState<Branch>(
    savedWorkspace.branch ?? "reviews",
  );
  const reviewLibraryQuery = useQuery({
    queryKey: ["review-library"],
    queryFn: dashboardApi.reviewLibrary,
  });
  const [productName, setProductName] = useState(
    savedWorkspace.productName ?? "",
  );
  const [brandName, setBrandName] = useState(savedWorkspace.brandName ?? "");
  const [category, setCategory] = useState(
    savedWorkspace.category ?? "美妆个护",
  );
  const [audience, setAudience] = useState(savedWorkspace.audience ?? "");
  const [benefits, setBenefits] = useState(savedWorkspace.benefits ?? "");
  const [assets, setAssets] = useState<ProductAsset[]>(
    savedWorkspace.assets ?? [],
  );
  const [referenceAssetIds, setReferenceAssetIds] = useState<string[]>(() => {
    const available = new Set(
      (savedWorkspace.assets ?? []).map((asset) => asset.id),
    );
    const saved = (savedWorkspace.referenceAssetIds ?? []).filter((id) =>
      available.has(id),
    );
    return saved.length
      ? saved.slice(0, 4)
      : (savedWorkspace.assets ?? []).slice(0, 4).map((asset) => asset.id);
  });
  const [selectedDirections, setSelectedDirections] = useState<Direction[]>(
    (savedWorkspace.selectedDirections?.length
      ? savedWorkspace.selectedDirections
      : ([
          "buyer",
          "model",
          "effect",
          "scene",
          "comparison",
          "retail",
        ] satisfies Direction[])
    ).slice(0, savedWorkspace.imageCount ?? 3),
  );
  const [reviewImageCount, setReviewImageCount] = useState(
    savedWorkspace.imageCount ?? 3,
  );
  const [taskImageCount, setTaskImageCount] = useState(
    savedWorkspace.taskImageCount ?? 3,
  );
  const [reviewCount, setReviewCount] = useState(
    savedWorkspace.reviewCount ?? 6,
  );
  const [reviewCountInput, setReviewCountInput] = useState(() =>
    String(savedWorkspace.reviewCount ?? 6),
  );
  const [authorized, setAuthorized] = useState(
    savedWorkspace.authorized ?? false,
  );
  const [drafts, setDrafts] = useState<ReviewDraft[]>(() =>
    restoreReviewDraftsForManualContinuation(savedWorkspace.drafts ?? []),
  );
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [pasteMessage, setPasteMessage] = useState("");
  const [referenceOpen, setReferenceOpen] = useState(false);
  const [modelTemplateOpen, setModelTemplateOpen] = useState(false);
  const [references, setReferences] = useState<ReferenceLink[]>(() =>
    savedWorkspace.references?.length
      ? savedWorkspace.references
      : readStorage("jianze-review-references-v1", []),
  );
  const [referenceDraft, setReferenceDraft] = useState({
    name: "",
    url: "",
    note: "",
  });
  const [history, setHistory] = useState<HistoryRecord[]>(() =>
    readStorage("jianze-review-history-v1", []),
  );
  const imageProvider: ImageProviderId = "openai";
  const [providerOpen, setProviderOpen] = useState(false);
  const [providerDraft, setProviderDraft] = useState({
    ...providerDefaults.openai,
    apiKey: "",
  });
  const [imageDirection, setImageDirection] = useState<Direction>(
    savedWorkspace.imageDirection ?? "buyer",
  );
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>(
    savedWorkspace.generatedImages ?? [],
  );
  const [reviewProgress, setReviewProgress] = useState({
    completed: 0,
    total: 0,
    active: 0,
    waiting: 0,
    failed: 0,
    concurrency: 0,
    startedAt: 0,
  });
  const [progressClock, setProgressClock] = useState(Date.now());
  const [previewImage, setPreviewImage] = useState<{
    gallery: Array<{ id: string; url: string }>;
    title: string;
    index: number;
  } | null>(null);
  const [competitorUrl, setCompetitorUrl] = useState(
    savedWorkspace.competitorUrl ?? "",
  );
  const [competitorAnalysis, setCompetitorAnalysis] =
    useState<AuthorizedShopReviewAnalysis | null>(
      savedWorkspace.competitorAnalysis?.source ===
        "pinduoduo-authorized-shop"
        ? savedWorkspace.competitorAnalysis
        : null,
    );
  const [competitorError, setCompetitorError] = useState("");
  const [publicProductReviewAnalysis, setPublicProductReviewAnalysis] =
    useState<PublicProductReviewAnalysis | null>(
      savedWorkspace.publicProductReviewAnalysis?.source ===
        "pinduoduo-public-product"
        ? savedWorkspace.publicProductReviewAnalysis
        : null,
    );
  const [publicProductReviewError, setPublicProductReviewError] = useState("");
  const [selectedLibraryId, setSelectedLibraryId] = useState<string | null>(
    null,
  );
  const [archiveAction, setArchiveAction] = useState<string | null>(null);
  const [librarySearch, setLibrarySearch] = useState("");
  const [libraryBatchMode, setLibraryBatchMode] = useState(false);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState<string[]>([]);
  const [generationRequirements, setGenerationRequirements] = useState(
    () =>
      savedWorkspace.generationRequirements ??
      localStorage.getItem("jianze-review-image-requirements-v1") ??
      defaultGenerationRequirements,
  );
  const [lockGroupModel, setLockGroupModel] = useState(
    () => savedWorkspace.lockGroupModel ?? true,
  );
  const [directionRequirements, setDirectionRequirements] = useState<
    Record<Direction, string>
  >(savedWorkspace.directionRequirements ?? readDirectionRequirements);
  const [directionContent, setDirectionContent] = useState<DirectionContent>(
    savedWorkspace.directionContent ?? readDirectionContent,
  );
  const [reviewRequirements, setReviewRequirements] = useState(
    () =>
      savedWorkspace.reviewRequirements ??
      localStorage.getItem("jianze-review-text-requirements-v1") ??
      defaultReviewRequirements,
  );
  const [endorsementSettings, setEndorsementSettings] =
    useState<ReviewEndorsementSettings>(() =>
      restoreReviewEndorsementSettings(savedWorkspace.endorsementSettings),
    );
  const [editingDirection, setEditingDirection] = useState<Direction | null>(
    null,
  );
  const [imageError, setImageError] = useState("");
  const [imageNotice, setImageNotice] = useState("");
  const [generationPaused, setGenerationPaused] = useState(false);
  const [generationManuallyPaused, setGenerationManuallyPaused] =
    useState(false);
  const [reviewRunPhase, setReviewRunPhase] = useState<ReviewRunPhase>(() => {
    const summary = reviewDraftSlotSummary(drafts);
    if (!summary.total) return "idle";
    if (summary.ready === summary.total) return "completed";
    if (summary.failed) return summary.ready ? "partial" : "failed";
    return "paused";
  });
  const [selectedDraftIds, setSelectedDraftIds] = useState<string[]>(
    savedWorkspace.selectedDraftIds ?? [],
  );
  const [workspaceHydrated, setWorkspaceHydrated] = useState(false);
  const [bulkAction, setBulkAction] = useState<"save" | "download" | null>(
    null,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const generationPausedRef = useRef(false);
  const generationRunRef = useRef(0);
  const generationAbortControllersRef = useRef(new Set<AbortController>());

  const configuredProvider = providersQuery.data?.find(
    (provider) => provider.provider === imageProvider,
  );
  const providerSetupError = imageProviderSetupError(
    Boolean(configuredProvider?.hasApiKey),
    providerDraft.apiKey,
  );
  const showProviderSetupError =
    !providersQuery.isPending &&
    !providersQuery.isError &&
    Boolean(providerSetupError);
  const selectedReferenceAssets = useMemo(
    () =>
      referenceAssetIds.flatMap((id) => {
        const asset = assets.find((value) => value.id === id);
        return asset ? [asset] : [];
      }),
    [assets, referenceAssetIds],
  );
  const reviewSlotSummary = useMemo(
    () => reviewDraftSlotSummary(drafts),
    [drafts],
  );
  const selectedLibraryItem =
    reviewLibraryQuery.data?.find((item) => item.id === selectedLibraryId) ??
    null;
  const filteredLibraryItems = useMemo(
    () => filterReviewLibrary(reviewLibraryQuery.data ?? [], librarySearch),
    [librarySearch, reviewLibraryQuery.data],
  );
  const selectedLibraryIdSet = useMemo(
    () => new Set(selectedLibraryIds),
    [selectedLibraryIds],
  );
  const selectedLibraryGroups = useMemo(
    () =>
      selectedReviewLibraryItems(
        reviewLibraryQuery.data ?? [],
        selectedLibraryIds,
      ),
    [reviewLibraryQuery.data, selectedLibraryIds],
  );
  const allFilteredLibraryItemsSelected =
    filteredLibraryItems.length > 0 &&
    filteredLibraryItems.every((item) => selectedLibraryIdSet.has(item.id));
  const savedSourceDraftIds = useMemo(
    () =>
      new Set(reviewLibraryQuery.data?.map((item) => item.sourceDraftId) ?? []),
    [reviewLibraryQuery.data],
  );
  useEffect(() => {
    setReviewCountInput(String(reviewCount));
  }, [reviewCount]);
  useEffect(() => {
    void loadHistoryRecords()
      .then((records) => setHistory(records.slice(0, 20)))
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    let active = true;
    void loadReviewWorkspace().then((snapshot) => {
      if (!active) return;
      if (snapshot.branch) setBranch(snapshot.branch);
      if (typeof snapshot.productName === "string")
        setProductName(snapshot.productName);
      if (typeof snapshot.brandName === "string")
        setBrandName(snapshot.brandName);
      if (typeof snapshot.category === "string") setCategory(snapshot.category);
      if (typeof snapshot.audience === "string") setAudience(snapshot.audience);
      if (typeof snapshot.benefits === "string") setBenefits(snapshot.benefits);
      if (snapshot.assets) setAssets(snapshot.assets);
      if (snapshot.referenceAssetIds)
        setReferenceAssetIds(snapshot.referenceAssetIds);
      if (snapshot.selectedDirections?.length)
        setSelectedDirections(snapshot.selectedDirections);
      if (typeof snapshot.imageCount === "number")
        setReviewImageCount(snapshot.imageCount);
      if (typeof snapshot.taskImageCount === "number")
        setTaskImageCount(snapshot.taskImageCount);
      if (typeof snapshot.reviewCount === "number")
        setReviewCount(snapshot.reviewCount);
      if (typeof snapshot.authorized === "boolean")
        setAuthorized(snapshot.authorized);
      if (snapshot.drafts) {
        setDrafts(snapshot.drafts);
        const summary = reviewDraftSlotSummary(snapshot.drafts);
        setReviewRunPhase(
          !summary.total
            ? "idle"
            : summary.ready === summary.total
              ? "completed"
              : summary.failed
                ? summary.ready
                  ? "partial"
                  : "failed"
                : "paused",
        );
      }
      if (typeof snapshot.competitorUrl === "string")
        setCompetitorUrl(snapshot.competitorUrl);
      if (snapshot.competitorAnalysis?.source === "pinduoduo-authorized-shop")
        setCompetitorAnalysis(snapshot.competitorAnalysis);
      if (
        snapshot.publicProductReviewAnalysis?.source ===
        "pinduoduo-public-product"
      )
        setPublicProductReviewAnalysis(snapshot.publicProductReviewAnalysis);
      if (snapshot.references) setReferences(snapshot.references);
      if (snapshot.imageDirection) setImageDirection(snapshot.imageDirection);
      if (snapshot.generatedImages)
        setGeneratedImages(snapshot.generatedImages);
      if (typeof snapshot.generationRequirements === "string")
        setGenerationRequirements(snapshot.generationRequirements);
      if (typeof snapshot.lockGroupModel === "boolean")
        setLockGroupModel(snapshot.lockGroupModel);
      if (snapshot.directionRequirements)
        setDirectionRequirements({
          ...defaultDirectionRequirements,
          ...snapshot.directionRequirements,
        });
      if (snapshot.directionContent)
        setDirectionContent({
          ...defaultDirectionContent,
          ...snapshot.directionContent,
        });
      if (typeof snapshot.reviewRequirements === "string")
        setReviewRequirements(snapshot.reviewRequirements);
      if (snapshot.endorsementSettings)
        setEndorsementSettings(
          restoreReviewEndorsementSettings(snapshot.endorsementSettings),
        );
      if (snapshot.selectedDraftIds)
        setSelectedDraftIds(snapshot.selectedDraftIds);
      setWorkspaceHydrated(true);
    });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    localStorage.setItem(
      "jianze-review-image-requirements-v1",
      generationRequirements,
    );
  }, [generationRequirements]);
  useEffect(() => {
    if (!previewImage) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPreviewImage(null);
      if (event.key === "ArrowLeft")
        setPreviewImage((current) =>
          current
            ? {
                ...current,
                index:
                  (current.index - 1 + current.gallery.length) %
                  current.gallery.length,
              }
            : null,
        );
      if (event.key === "ArrowRight")
        setPreviewImage((current) =>
          current
            ? {
                ...current,
                index: (current.index + 1) % current.gallery.length,
              }
            : null,
        );
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [Boolean(previewImage)]);
  useEffect(() => {
    localStorage.setItem(
      "jianze-review-direction-requirements-v1",
      JSON.stringify(directionRequirements),
    );
  }, [directionRequirements]);
  useEffect(() => {
    localStorage.setItem(
      "jianze-review-direction-content-v1",
      JSON.stringify(directionContent),
    );
  }, [directionContent]);
  useEffect(() => {
    localStorage.setItem(
      "jianze-review-text-requirements-v1",
      reviewRequirements,
    );
  }, [reviewRequirements]);
  useEffect(() => {
    if (!workspaceHydrated) return;
    void persistReviewWorkspace({
      branch,
      productName,
      brandName,
      category,
      audience,
      benefits,
      assets,
      referenceAssetIds,
      selectedDirections,
      imageCount: reviewImageCount,
      reviewCount,
      authorized,
      drafts,
      competitorUrl,
      competitorAnalysis,
      publicProductReviewAnalysis,
      reviewRequirements,
      endorsementSettings,
      taskImageCount,
      references,
      imageDirection,
      generatedImages,
      generationRequirements,
      lockGroupModel,
      directionRequirements,
      directionContent,
      selectedDraftIds,
    }).catch(() => {
      /* keep the current session usable if browser storage is unavailable */
    });
  }, [
    workspaceHydrated,
    branch,
    productName,
    brandName,
    category,
    audience,
    benefits,
    assets,
    referenceAssetIds,
    selectedDirections,
    reviewImageCount,
    reviewCount,
    authorized,
    drafts,
    competitorUrl,
    competitorAnalysis,
    publicProductReviewAnalysis,
    reviewRequirements,
    endorsementSettings,
    taskImageCount,
    references,
    imageDirection,
    generatedImages,
    generationRequirements,
    lockGroupModel,
    directionRequirements,
    directionContent,
    selectedDraftIds,
  ]);
  useEffect(() => {
    const provider = providersQuery.data?.find(
      (value) => value.provider === "openai",
    );
    setProviderDraft({
      ...(provider
        ? {
            provider: provider.provider,
            label: provider.label,
            baseUrl: provider.baseUrl,
            model: provider.model,
            enabled: provider.enabled,
          }
        : providerDefaults.openai),
      apiKey: "",
    });
  }, [providersQuery.data]);
  const sellingPoints = useMemo(
    () =>
      benefits
        .split(/[，,；;、\n]/)
        .map((item) => item.trim())
        .filter(Boolean),
    [benefits],
  );
  const updateSellingPoint = (index: number, value: string) => {
    const next = [...sellingPoints];
    next[index] = value;
    setBenefits(next.join("、"));
  };
  const removeSellingPoint = (index: number) => {
    setBenefits(
      sellingPoints.filter((_, itemIndex) => itemIndex !== index).join("、"),
    );
  };
  const addSellingPoint = () => {
    setBenefits([...sellingPoints, "新卖点"].join("、"));
  };
  const directionItems = useMemo(
    () =>
      directions.map((item) => ({ ...item, ...directionContent[item.key] })),
    [directionContent],
  );
  const addFiles = async (files: FileList | File[]) => {
    const accepted = [...files]
      .filter((file) => file.type.startsWith("image/"))
      .slice(0, Math.max(0, 8 - assets.length));
    const next = await Promise.all(
      accepted.map(
        (file) =>
          new Promise<ProductAsset>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                id: `${file.name}-${file.lastModified}-${Math.random()}`,
                name: file.name,
                url: String(reader.result),
                size: file.size,
              });
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
          }),
      ),
    );
    setAssets((current) => [...current, ...next].slice(0, 8));
    setReferenceAssetIds((current) =>
      appendProductReferenceIds(
        current,
        next.map((asset) => asset.id),
      ),
    );
    if (next.length) {
      setImageError("");
      setImageNotice("图片已添加并自动作为产品参考图，可保留 1 张或多选");
    }
    return next.length;
  };
  const removeAsset = (id: string) => {
    setAssets((current) => current.filter((asset) => asset.id !== id));
    setReferenceAssetIds((current) =>
      current.filter((assetId) => assetId !== id),
    );
  };
  const toggleReferenceAsset = (id: string) => {
    if (!referenceAssetIds.includes(id) && referenceAssetIds.length >= 4) {
      setImageError("一次最多选择 4 张产品参考图；只保留 1 张也可以");
      return;
    }
    setImageError("");
    setReferenceAssetIds((current) =>
      current.includes(id)
        ? current.filter((assetId) => assetId !== id)
        : [...current, id],
    );
  };
  const moveReferenceAsset = (id: string, offset: -1 | 1) => {
    setReferenceAssetIds((current) => {
      const from = current.indexOf(id);
      const to = from + offset;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const next = [...current];
      [next[from], next[to]] = [next[to]!, next[from]!];
      return next;
    });
  };
  const pasteFiles = async (files: File[]) => {
    if (!files.length) {
      setPasteMessage("剪贴板中没有图片");
      return;
    }
    const added = await addFiles(files);
    setPasteMessage(
      added ? `已粘贴 ${added} 张图片并选为参考图` : "最多保留 8 张产品图片",
    );
    window.setTimeout(() => setPasteMessage(""), 2_000);
  };
  const fileFromClipboardSource = async (source: string, index: number) => {
    if (/^https?:/i.test(source)) {
      const imported = await dashboardApi.importClipboardImage(source);
      const response = await fetch(imported.dataUrl);
      const blob = await response.blob();
      return new File([blob], imported.name, { type: imported.mimeType });
    }
    const response = await fetch(source);
    if (!response.ok) throw new Error("复制的图片无法读取");
    const blob = await response.blob();
    if (!blob.type.startsWith("image/")) {
      throw new Error("复制内容不是图片");
    }
    const extension = blob.type.split("/")[1] || "png";
    return new File(
      [blob],
      `剪贴板产品图-${Date.now()}-${index + 1}.${extension}`,
      { type: blob.type },
    );
  };
  const pasteImageSources = async (sources: string[]) => {
    const available = Math.max(0, 8 - assets.length);
    if (!available) {
      setPasteMessage("最多保留 8 张产品图片");
      return;
    }
    setPasteMessage("正在读取复制的网页图片…");
    const results = await Promise.allSettled(
      sources
        .slice(0, available)
        .map((source, index) => fileFromClipboardSource(source, index)),
    );
    const files = results.flatMap((result) =>
      result.status === "fulfilled" ? [result.value] : [],
    );
    if (!files.length) {
      const failure = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      );
      setPasteMessage(
        failure?.reason instanceof Error
          ? failure.reason.message
          : "复制内容中没有可读取的图片",
      );
      return;
    }
    await pasteFiles(files);
  };
  const pasteFromClipboard = async () => {
    try {
      if (!navigator.clipboard?.read) {
        setPasteMessage("请在此区域按 Command+V 粘贴图片");
        return;
      }
      const items = await navigator.clipboard.read();
      const files: File[] = [];
      const html: string[] = [];
      const text: string[] = [];
      for (const item of items)
        for (const type of item.types) {
          if (type.startsWith("image/")) {
            const blob = await item.getType(type);
            files.push(
              new File(
                [blob],
                `剪贴板图片-${Date.now()}-${files.length + 1}.${type.split("/")[1] ?? "png"}`,
                { type },
              ),
            );
          } else if (type === "text/html") {
            html.push(await (await item.getType(type)).text());
          } else if (type === "text/plain") {
            text.push(await (await item.getType(type)).text());
          }
        }
      if (files.length) {
        await pasteFiles(files);
        return;
      }
      const sources = clipboardImageSources(
        html.join("\n"),
        text.join("\n"),
        window.location.href,
      );
      if (!sources.length) {
        setPasteMessage("剪贴板中没有图片或图片链接");
        return;
      }
      await pasteImageSources(sources);
    } catch {
      setPasteMessage("请允许读取剪贴板，或在此区域按 Command+V");
    }
  };
  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const files = [...event.clipboardData.items]
      .filter((item) => item.type.startsWith("image/"))
      .flatMap((item, index) => {
        const file = item.getAsFile();
        return file
          ? [
              new File(
                [file],
                file.name || `粘贴图片-${Date.now()}-${index + 1}.png`,
                { type: file.type },
              ),
            ]
          : [];
      });
    if (files.length) {
      event.preventDefault();
      void pasteFiles(files);
      return;
    }
    const sources = clipboardImageSources(
      event.clipboardData.getData("text/html"),
      event.clipboardData.getData("text/plain"),
      window.location.href,
    );
    if (sources.length) {
      event.preventDefault();
      void pasteImageSources(sources);
    } else {
      setPasteMessage("复制内容中没有图片或图片链接");
    }
  };
  const replaceHistory = (
    updater: (current: HistoryRecord[]) => HistoryRecord[],
  ) => {
    setHistory((current) => {
      const next = updater(current).slice(0, 20);
      void persistHistoryRecords(next).catch(() => undefined);
      return next;
    });
  };
  const createReviewDrafts = async () => {
    const previousReviews = [
      ...history.flatMap((record) => record.reviews),
      ...drafts.map((draft) => draft.text),
    ];
    let texts: string[];
    try {
      const result = await dashboardApi.generateReviewTexts({
        product: productName,
        audience,
        benefits,
        count: reviewCount,
        previousReviews,
        requirements: reviewRequirements || defaultReviewRequirements,
        brandName,
        competitorThemes: reviewReferenceThemes(publicProductReviewAnalysis, competitorAnalysis),
      });
      texts = selectDiverseReviewTexts(result.texts, previousReviews, reviewCount);
      if (texts.length < reviewCount) {
        throw new Error("文本模型返回了重复或近似句式的评价");
      }
    } catch {
      texts = naturalReviews(
        productName, audience, benefits, reviewCount,
        reviewReferenceThemes(publicProductReviewAnalysis, competitorAnalysis),
        previousReviews, reviewRequirements, brandName, endorsementSettings,
      );
    }
    const createdAt = new Date().toISOString();
    const batchId = `${Date.now()}-${crypto.randomUUID().slice(0, 8)}`;
    const allocatedDirections = allocateDirections(
      selectedDirections.slice(0, reviewImageCount),
      reviewImageCount,
    );
    const next: ReviewDraft[] = texts.map((text, index) => {
      const modelIdentity = buildReviewGroupModelIdentity(batchId, index);
      return {
        id: `${batchId}-${index}`,
        batchId,
        text,
        createdAt,
        directions: [...new Set(allocatedDirections)],
        imageCount: reviewImageCount,
        ...(lockGroupModel
          ? {
              modelIdentity,
              modelWardrobe: buildReviewGroupWardrobe(modelIdentity),
            }
          : {}),
        imageSlots: allocatedDirections.map((direction, imageIndex) => ({
          id: `${batchId}-${index}-${imageIndex}`,
          direction,
          status: "waiting" as const,
          image: null,
          error: null,
        })),
      };
    });
    setDrafts(next);
    setSelectedDraftIds([]);
    const record: HistoryRecord = {
      id: batchId,
      productName,
      brandName,
      category,
      audience,
      benefits,
      count: reviewCount,
      imageCount: reviewImageCount,
      createdAt,
      reviews: texts,
      status: "running",
      assets,
      referenceAssetIds,
      selectedDirections: [...new Set(allocatedDirections)],
      authorized,
      drafts: next,
      competitorUrl,
      competitorAnalysis,
      publicProductReviewAnalysis,
      reviewRequirements,
      endorsementSettings,
      generationRequirements,
      lockGroupModel,
      directionRequirements,
      directionContent,
    };
    replaceHistory((current) => [
      record,
      ...current.filter((item) => item.id !== batchId),
    ]);
    return next;
  };
  const copy = async (draft: ReviewDraft) => {
    await navigator.clipboard.writeText(draft.text);
    setCopiedId(draft.id);
    window.setTimeout(() => setCopiedId(null), 1_500);
  };
  const addReference = () => {
    if (!referenceDraft.url.trim()) return;
    const next = [
      ...references,
      {
        id: `${Date.now()}`,
        name: referenceDraft.name.trim() || "同行参考",
        url: referenceDraft.url.trim(),
        note: referenceDraft.note.trim(),
      },
    ];
    setReferences(next);
    localStorage.setItem("jianze-review-references-v1", JSON.stringify(next));
    setReferenceDraft({ name: "", url: "", note: "" });
  };
  const showImageNotice = (message: string) => {
    setImageNotice(message);
    window.setTimeout(() => setImageNotice(""), 2_000);
  };
  const downloadReviewArchive = async (
    groups: ReviewArchiveGroup[],
    actionId: string,
  ) => {
    if (archiveAction) return;
    setArchiveAction(actionId);
    setImageError("");
    try {
      const archive = await createReviewArchive(groups);
      triggerReviewArchiveDownload(archive, reviewArchiveFileName(groups));
      showImageNotice(`已打包下载 ${groups.length} 组图文原稿`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "压缩包生成失败，请重试";
      setImageError(message);
      showImageNotice(message);
    } finally {
      setArchiveAction(null);
    }
  };
  const providerInput = (): ImageProviderInput => ({
    provider: providerDraft.provider,
    label: providerDraft.label.trim(),
    baseUrl: providerDraft.baseUrl.trim(),
    model: providerDraft.model.trim(),
    enabled: providerDraft.enabled,
    ...(providerDraft.apiKey.trim()
      ? { apiKey: providerDraft.apiKey.trim() }
      : {}),
  });
  const saveProvider = useMutation({
    mutationFn: () =>
      dashboardApi.saveImageProvider(imageProvider, providerInput()),
    onSuccess: (provider) => {
      queryClient.setQueryData(
        ["image-providers"],
        (current: typeof providersQuery.data) => [
          ...(current ?? []).filter(
            (value) => value.provider !== provider.provider,
          ),
          provider,
        ],
      );
      setProviderDraft((current) => ({ ...current, apiKey: "" }));
      setProviderOpen(false);
      setImageError("");
      showImageNotice(`${providerLabels[provider.provider]}接口已保存`);
    },
    onError: () =>
      setImageError("保存失败，请检查 API Key、接口地址和模型名称"),
  });
  const testProvider = useMutation({
    mutationFn: async () => {
      await dashboardApi.saveImageProvider(imageProvider, providerInput());
      return dashboardApi.testImageProvider(
        imageProvider,
        reviewImageProviderTestOperation,
      );
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["image-providers"] });
      setProviderDraft((current) => ({ ...current, apiKey: "" }));
      const error = imageProviderTestError(result);
      setImageError(error);
      if (!error) showImageNotice(result.message);
    },
    onError: (error) =>
      setImageError(
        error instanceof Error
          ? `连接检测失败：${error.message}`
          : "连接检测失败，请检查本地服务是否正在运行",
      ),
  });
  const submitProviderConfiguration = (action: "save" | "test") => {
    if (providersQuery.isPending) {
      setImageError("正在读取 GPT Image 2 接口配置，请稍候再试");
      return;
    }
    if (providersQuery.isError) {
      setImageError("GPT Image 2 接口配置读取失败，请刷新页面后重试");
      return;
    }
    if (providerSetupError) {
      setImageError(providerSetupError);
      return;
    }
    setImageError("");
    if (action === "test") testProvider.mutate();
    else saveProvider.mutate();
  };
  const requireConfiguredImageProvider = () => {
    if (providersQuery.isPending) {
      setImageError("正在读取 GPT Image 2 接口配置，请稍候再试");
      return true;
    }
    if (providersQuery.isError) {
      setImageError("GPT Image 2 接口配置读取失败，请刷新页面后重试");
      if (canManageProvider) setProviderOpen(true);
      return true;
    }
    if (!configuredProvider?.enabled || !configuredProvider.hasApiKey) {
      setImageError(
        canManageProvider
          ? imageProviderMissingKeyMessage
          : "GPT Image 2 接口尚未配置，请联系管理员",
      );
      if (canManageProvider) setProviderOpen(true);
      return true;
    }
    return false;
  };
  const competitorReviewAnalysis = useMutation({
    mutationFn: dashboardApi.analyzeAuthorizedShopReviews,
    onSuccess: (result) => {
      setCompetitorAnalysis(result);
      setCompetitorError("");
    },
    onError: (error) => {
      setCompetitorAnalysis(null);
      setCompetitorError(
        error instanceof Error ? error.message : "拼多多授权店铺评价分析失败",
      );
    },
  });
  const publicProductReviewMutation = useMutation({
    mutationFn: (url: string) =>
      dashboardApi.analyzePublicProductReviews({ url }),
    onSuccess: (result) => {
      setPublicProductReviewAnalysis(result);
      setPublicProductReviewError("");
    },
    onError: (error) => {
      setPublicProductReviewAnalysis(null);
      setPublicProductReviewError(
        error instanceof Error ? error.message : "拼多多公开商品评价分析失败",
      );
    },
  });
  const saveReviewToLibrary = useMutation({
    mutationFn: (draft: ReviewDraft) => {
      const generatedOriginals = draft.imageSlots.flatMap((slot) =>
        slot.image ? [slot.image] : [],
      );
      if (!generatedOriginals.length) {
        throw new Error("请先生成至少一张原图，再保存到素材数据库");
      }
      return dashboardApi.saveReviewLibrary({
        sourceDraftId: draft.id,
        productName: productName.trim(),
        reviewText: draft.text,
        directions: directionItems
          .filter((item) => draft.directions.includes(item.key))
          .map((item) => item.title),
        images: generatedOriginals,
        competitorUrl: publicProductReviewAnalysis?.productUrl ?? null,
      });
    },
    onMutate: () => setImageError(""),
    onSuccess: (savedItem, draft) => {
      queryClient.setQueryData(
        ["review-library"],
        (current: typeof reviewLibraryQuery.data) => [
          savedItem,
          ...(current ?? []).filter(
            (item) => item.sourceDraftId !== savedItem.sourceDraftId,
          ),
        ],
      );
      showImageNotice("已保存到素材数据库");
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "保存数据库失败";
      setImageError(message);
      showImageNotice(message);
    },
  });
  const saveGeneratedTask = useMutation({
    mutationFn: async () => {
      if (!generatedImages.length) {
        throw new Error("请先生成至少一张原图，再保存到素材数据库");
      }
      const batches = Array.from(
        { length: Math.ceil(generatedImages.length / 4) },
        (_, index) => generatedImages.slice(index * 4, index * 4 + 4),
      );
      return Promise.all(
        batches.map((images, index) =>
          dashboardApi.saveMainImageLibrary({
            sourceBatchId: `review-studio-${Date.now()}-${index}`,
            requirements: `${productName.trim()} · ${directionContent[imageDirection].title} · ${generationRequirements}`,
            images,
          }),
        ),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["main-image-library"] });
      showImageNotice("图片任务已保存到素材数据库");
    },
    onError: (error) =>
      setImageError(
        error instanceof Error ? error.message : "保存图片任务失败",
      ),
  });
  const deleteReviewFromLibrary = useMutation({
    mutationFn: (id: string) => dashboardApi.deleteReviewLibrary(id),
    onSuccess: (_, id) =>
      queryClient.setQueryData(
        ["review-library"],
        (current: typeof reviewLibraryQuery.data) =>
          (current ?? []).filter((item) => item.id !== id),
      ),
  });
  const imageGeneration = useMutation({
    mutationFn: async () => {
      const referenceImages = await prepareImageGenerationReferences(
        selectedReferenceAssets.map((asset) => asset.url),
      );
      const results = await runBoundedImageTasks(
        taskImageCount,
        standaloneImageInitialConcurrency,
        async (index) => {
          return requestSingleReviewImage(
            buildStrictReviewImageRequest(
              imageDirection,
              imagePrompt(
                imageDirection,
                `standalone-${index}-${crypto.randomUUID()}`,
              ),
              referenceImages,
            ),
            dashboardApi.generateImages,
          );
        },
      );
      const images = results.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      );
      const errors = results.flatMap((result) =>
        result.status === "rejected"
          ? [
              result.reason instanceof Error
                ? result.reason.message
                : "图片生成失败",
            ]
          : [],
      );
      return {
        images,
        failed: errors.length,
        total: results.length,
        firstError: errors[0] ?? null,
      };
    },
    onSuccess: (result) => {
      setGeneratedImages(result.images);
      void queryClient.invalidateQueries({
        queryKey: ["generated-image-archive"],
      });
      setImageError(
        result.failed
          ? `${result.images.length}/${result.total} 张原图已返回，其中 ${result.failed} 张生成失败。${result.firstError ?? "请稍后重试"}`
          : "",
      );
      showImageNotice(
        result.failed
          ? `已完成 ${result.images.length}/${result.total} 张`
          : `已生成 ${result.images.length} 张图片`,
      );
    },
    onError: (error) =>
      setImageError(
        error instanceof Error ? error.message : "图片生成失败，请检查接口配置",
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["generated-image-archive"],
      });
    },
  });
  const imagePrompt = (
    direction: Direction,
    uniqueKey: string,
    modelIdentity?: string,
    modelWardrobe?: string,
  ) => {
    const referenceRule = referenceStructureRequirement(
      selectedReferenceAssets.length > 0,
    );
    const structureRule = productStructureRequirement(productName);
    const anglesByDirection: Record<Direction, string[]> = {
      buyer: [
        "买家站立时随手拍摄的略微俯视角度",
        "自然平视但构图略有偏移",
        "略带手机镜头透视的斜侧角度",
        "坐着记录产品时自然向下拍摄",
      ],
      model: [
        "与人物眼睛接近高度的正面手机视角",
        "略高于眼睛但人物正脸看镜头的视角",
        "自然平视、同时看清人物眼神与产品正面的视角",
        "轻微斜侧但人物脸部仍朝向镜头的半身视角",
      ],
      effect: [
        "从护理者肩后看向单一护理动作的侧面视角",
        "贴近使用部位且能交代桌面产品位置的近距离视角",
        "坐在桌边自然向下记录护理过程的视角",
        "不遮挡人物手臂连接关系的斜侧视角",
      ],
      scene: [
        "自然平视但构图略有偏移",
        "略带手机镜头透视的斜侧角度",
        "从使用位置外侧拍到产品与真实环境",
        "站立时随手拍摄的略微俯视角度",
      ],
      comparison: [
        "正面平视的固定机位左右对比视角",
        "贴近同一使用部位的等距对比视角",
        "自然侧光下保持相同距离的前后对比视角",
        "普通手机固定位置拍摄的左右分栏视角",
      ],
      retail: [
        "从购物通道斜侧拍向促销端架的手机视角",
        "与货架中层接近平齐的自然平视角度",
        "略低机位拍摄商超特卖堆头的现场视角",
        "顾客停在促销台前随手记录的略微俯视角度",
      ],
    };
    const lighting = [
      "窗边自然光与室内灯光混合",
      "阴天窗边柔和自然光",
      "普通家用暖光灯",
      "清晨侧光并保留真实阴影",
      "傍晚室内自然偏暖光线",
      "亮度不均匀的真实手机随拍光线",
    ];
    const retailLighting = [
      "卖场顶灯与货架灯带形成的真实混合光",
      "免税店明亮但略有色差的室内照明",
      "大型商超均匀顶灯并保留货架投影",
      "促销台射灯与购物通道环境光混合",
    ];
    const environments = [
      "符合该品类的真实日常使用位置",
      "带少量同类日用品的普通台面",
      "家中实际收纳产品的位置",
      "拆开快递后的普通桌面",
      "符合目标人群的随身携带场景",
      "不过度整理的日常生活角落",
    ];
    const retailEnvironments = [
      "机场或口岸免税店的开放式货架区",
      "大型商超的限时特卖端架",
      "大型卖场通道旁的促销堆头",
      "商超收银区附近的临时促销台",
    ];
    const actionsByDirection: Record<Direction, string[]> = {
      buyer: [
        "刚拿起产品准备使用",
        "拆封后单手查看包装",
        "用完后把产品放在台面上记录",
        "放回日常位置前顺手拍摄",
      ],
      model: [
        "自然单手握住产品并看向镜头",
        "把产品轻靠在掌心并正脸看向镜头展示",
        "坐着单手展示产品正面并与镜头自然对视",
        "站在真实生活环境中看向镜头展示产品",
      ],
      effect: [
        "产品正向放在桌面，人物只专注一个护理动作",
        "产品正向放在使用位置旁，只展示参考图明确支持的使用结果",
        "人物看向使用部位，产品不在手中并保持正向静态展示",
        "完成取用后把产品正向放回桌面，再记录单一使用动作",
      ],
      scene: [
        "把产品放回日常位置后顺手拍摄",
        "产品自然放在同品类日用品之间",
        "拆封后把产品摆在普通桌面记录",
        "产品放在真实收纳位置并保留生活环境",
      ],
      comparison: [
        "在同一张图左右两侧记录使用前与使用后的真实状态",
        "保持同一人物和使用部位，记录自然克制的用后变化",
        "固定机位与光线，对照使用前后同一部位",
        "产品正向放在画面中，并呈现同一对象前后状态",
      ],
      retail: [
        "产品正向摆在特卖端架的主展示位",
        "产品陈列在促销台前排并保留真实购物通道",
        "顾客经过货架时用手机记录限时特卖陈列",
        "产品稳固摆在商超堆头上并突出线下促销氛围",
      ],
    };
    let hash = 0;
    for (const character of uniqueKey)
      hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
    const pick = (values: string[], offset: number) => {
      const mixed = Math.imul(hash ^ (offset * 0x9e3779b9), 0x85ebca6b) >>> 0;
      return values[
        ((mixed ^ (mixed >>> (offset + 3))) >>> 0) % values.length
      ]!;
    };
    const directionEnvironments =
      direction === "retail" ? retailEnvironments : environments;
    const directionLighting = direction === "retail" ? retailLighting : lighting;
    const variation = `${pick(anglesByDirection[direction], 1)}，${pick(directionLighting, 2)}，场景为${pick(directionEnvironments, 3)}，记录${pick(actionsByDirection[direction], 4)}。手势、背景物品、机位和光线必须与同组其他图片明显不同，不复用构图。`;
    const modelIdentityRule = buildReviewGroupModelPromptRule(
      modelIdentity,
      modelWardrobe,
    );
    return `为电商商品“${productName.trim()}”生成一张全新的真实买家视角图片。品类：${category}。目标人群：${audience.trim() || "普通消费者"}。创作方向：${directionContent[direction].title}，${directionContent[direction].detail}。${modelIdentityRule}本张独立拍摄变化：${variation}真实卖点：${sellingPoints.join("、") || "突出商品真实质感"}。方向详细要求：${directionRequirements[direction]}。${referenceRule}${structureRule}通用图片要求：${generationRequirements.trim() || defaultGenerationRequirements}。${physicalRealityRequirements}${directionRealityRequirements[direction]}照片应像普通买家用手机记录真实使用过程，允许轻微构图偏差、环境杂物、自然肤质和不完美细节；避免商业广告布光、模板化摆拍和明显AI感。商品主体清晰，不添加文字、水印、虚假认证或夸张功效表达。`;
  };
  const reviewGeneration = useMutation({
    mutationFn: async ({
      preparedDrafts,
      runId,
    }: {
      preparedDrafts: ReviewDraft[];
      runId: number;
    }) => {
      const referenceImages = await prepareImageGenerationReferences(
        selectedReferenceAssets.map((asset) => asset.url),
      );
      setReviewRunPhase(generationPausedRef.current ? "paused" : "running");
      const tasks = preparedDrafts.flatMap((draft) =>
        draft.imageSlots
          .filter((slot) => slot.status !== "ready")
          .map((slot) => ({
            draftId: draft.id,
            modelIdentity: reviewGroupModelIdentityForGeneration(
              lockGroupModel,
              draft.modelIdentity,
            ),
            modelWardrobe: lockGroupModel ? draft.modelWardrobe : undefined,
            slot,
          })),
      );
      let cursor = 0;
      let completed = 0;
      let failed = 0;
      let active = 0;
      let concurrencyState: ReviewImageConcurrencyState = {
        limit: Math.min(reviewImageInitialConcurrency, tasks.length),
        fastSuccesses: 0,
      };
      const startedAt = Date.now();
      const updateProgress = () => {
        if (!isCurrentReviewGenerationRun(generationRunRef.current, runId))
          return;
        setReviewProgress({
          completed,
          total: tasks.length,
          active,
          waiting: Math.max(0, tasks.length - cursor),
          failed,
          concurrency: concurrencyState.limit,
          startedAt,
        });
      };
      updateProgress();
      const worker = async (workerIndex: number) => {
        while (cursor < tasks.length && generationRunRef.current === runId) {
          while (
            shouldReviewImageWorkerWait(
              workerIndex,
              cursor,
              tasks.length,
              concurrencyState,
              generationPausedRef.current,
            ) &&
            generationRunRef.current === runId
          ) {
            await new Promise((resolve) => window.setTimeout(resolve, 180));
          }
          if (generationRunRef.current !== runId || cursor >= tasks.length)
            return;
          const task = tasks[cursor++]!;
          const taskStartedAt = Date.now();
          const controller = new AbortController();
          generationAbortControllersRef.current.add(controller);
          active += 1;
          updateProgress();
          setDrafts((current) =>
            current.map((draft) =>
              draft.id === task.draftId
                ? {
                    ...draft,
                    imageSlots: draft.imageSlots.map((slot) =>
                      slot.id === task.slot.id
                        ? {
                            ...slot,
                            status: "generating",
                            generationStartedAt: taskStartedAt,
                          }
                        : slot,
                    ),
                  }
                : draft,
            ),
          );
          let image: GeneratedImage | null = null;
          let errorMessage: string | null = null;
          let cancelled = false;
          try {
            image = await requestSingleReviewImage(
              buildStrictReviewImageRequest(
                task.slot.direction,
                imagePrompt(
                  task.slot.direction,
                  task.slot.id,
                  task.modelIdentity,
                  task.modelWardrobe,
                ),
                referenceImages,
              ),
              (request) =>
                dashboardApi.generateImages(request, controller.signal),
            );
          } catch (error) {
            cancelled =
              controller.signal.aborted || generationRunRef.current !== runId;
            if (!cancelled) {
              errorMessage =
                error instanceof Error ? error.message : "图片生成失败";
            }
          } finally {
            generationAbortControllersRef.current.delete(controller);
            active = Math.max(0, active - 1);
          }
          if (cancelled) {
            if (!isCurrentReviewGenerationRun(generationRunRef.current, runId))
              return;
            const cancellationReason = controller.signal.reason;
            const cancellationMessage =
              cancellationReason instanceof Error && cancellationReason.message
                ? cancellationReason.message
                : reviewGenerationStoppedMessage;
            setDrafts((current) =>
              current.map((draft) =>
                draft.id === task.draftId
                  ? {
                      ...draft,
                      imageSlots: draft.imageSlots.map((slot) =>
                        slot.id === task.slot.id
                          ? {
                              ...slot,
                              status: "waiting",
                              error: cancellationMessage,
                              generationStartedAt: null,
                            }
                          : slot,
                      ),
                    }
                  : draft,
              ),
            );
            updateProgress();
            return;
          }
          const generated = Boolean(image);
          setDrafts((current) =>
            current.map((draft) =>
              draft.id === task.draftId
                ? {
                    ...draft,
                    imageSlots: draft.imageSlots.map((slot) =>
                      slot.id === task.slot.id
                        ? {
                            ...slot,
                            status: generated ? "ready" : "error",
                            image: image ?? slot.image,
                            error: errorMessage,
                            generationStartedAt: null,
                          }
                        : slot,
                    ),
                  }
                : draft,
            ),
          );
          if (!generated) failed += 1;
          completed += 1;
          const elapsed = Date.now() - taskStartedAt;
          const underPressure = Boolean(
            errorMessage &&
            /429|限流|繁忙|超时|timeout|rate.?limit|too many/i.test(
              errorMessage,
            ),
          );
          concurrencyState = adjustReviewImageConcurrency(
            concurrencyState,
            underPressure
              ? "pressure"
              : image && elapsed < 120_000
                ? "fast-success"
                : "success",
          );
          updateProgress();
        }
      };
      await Promise.all(
        Array.from(
          { length: reviewImageWorkerCount(tasks.length) },
          (_, index) => worker(index),
        ),
      );
      return {
        completed: completed - failed,
        failed,
        total: tasks.length,
        readyBeforeRun: reviewDraftSlotSummary(preparedDrafts).ready,
        runId,
        cancelled: generationRunRef.current !== runId,
      };
    },
    onSuccess: (result) => {
      if (result.cancelled || result.runId !== generationRunRef.current) return;
      setGenerationPaused(false);
      setGenerationManuallyPaused(false);
      setReviewRunPhase(
        result.failed
          ? result.completed + result.readyBeforeRun === 0
            ? "failed"
            : "partial"
          : "completed",
      );
      setImageError(
        result.failed
          ? `${result.completed} 张已生成，${result.failed} 张生成失败`
          : "",
      );
      showImageNotice(
        result.failed
          ? `已生成 ${result.completed}/${result.total} 张`
          : `已生成 ${result.total} 张图片和 ${reviewCount} 条评语`,
      );
    },
    onError: (error, variables) => {
      if (
        !isCurrentReviewGenerationRun(generationRunRef.current, variables.runId)
      )
        return;
      const message = error instanceof Error ? error.message : "图文生成失败";
      setImageError(message);
      setGenerationPaused(false);
      setGenerationManuallyPaused(false);
      setReviewRunPhase("failed");
      setDrafts((current) => markReviewDraftSlotsFailed(current, message));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["generated-image-archive"],
      });
    },
  });
  useEffect(() => {
    const batchId = drafts[0]?.batchId;
    if (!batchId) return;
    const status = resolveReviewHistoryStatus(drafts, reviewRunPhase);
    replaceHistory((current) =>
      current.map((record) =>
        record.id === batchId
              ? {
                  ...record,
                  status,
                  drafts,
                  reviews: drafts.map((draft) => draft.text),
                  endorsementSettings,
                }
          : record,
      ),
    );
  }, [drafts, reviewRunPhase, endorsementSettings]);
  const regenerateReviewText = async (draftId: string) => {
    const draftIndex = drafts.findIndex((draft) => draft.id === draftId);
    const previous = [
      ...history.flatMap((record) => record.reviews),
      ...drafts
        .filter((draft) => draft.id !== draftId)
        .map((draft) => draft.text),
    ];
    let text: string;
    try {
      const generated = (await dashboardApi.generateReviewTexts({
        product: productName,
        audience,
        benefits,
        count: 1,
        previousReviews: previous,
        requirements: reviewRequirements || defaultReviewRequirements,
        brandName: shouldMentionReviewBrand(draftIndex, drafts.length, brandName) ? brandName : "",
        competitorThemes: reviewReferenceThemes(publicProductReviewAnalysis, competitorAnalysis),
      })).texts;
      const diverse = selectDiverseReviewTexts(generated, previous, 1);
      if (diverse.length < 1) {
        throw new Error("文本模型返回了重复或近似句式的评价");
      }
      text = diverse[0]!;
    } catch {
      text = naturalReviews(
        productName, audience, benefits, 1,
        reviewReferenceThemes(publicProductReviewAnalysis, competitorAnalysis),
        previous, reviewRequirements, shouldMentionReviewBrand(draftIndex, drafts.length, brandName) ? brandName : "", endorsementSettings,
      )[0]!;
    }
    setDrafts((current) =>
      current.map((draft) =>
        draft.id === draftId ? { ...draft, text } : draft,
      ),
    );
  };
  const retryReviewSlot = async (draftId: string, slotId: string) => {
    const sourceDraft = drafts.find((draft) => draft.id === draftId);
    const slot = sourceDraft?.imageSlots.find((value) => value.id === slotId);
    if (!slot) return;
    setReviewRunPhase("preflight");
    setImageError("");
    const generationStartedAt = Date.now();
    setDrafts((current) =>
      current.map((draft) =>
        draft.id === draftId
          ? {
              ...draft,
              imageSlots: draft.imageSlots.map((value) =>
                value.id === slotId
                  ? {
                      ...value,
                      status: "generating",
                      error: null,
                      generationStartedAt,
                    }
                  : value,
              ),
            }
          : draft,
      ),
    );
    try {
      setReviewRunPhase("running");
      const referenceImages = await prepareImageGenerationReferences(
        selectedReferenceAssets.map((asset) => asset.url),
      );
      const image = await requestSingleReviewImage(
        buildStrictReviewImageRequest(
          slot.direction,
          imagePrompt(
            slot.direction,
            `${slot.id}-manual-${Date.now()}`,
            reviewGroupModelIdentityForGeneration(
              lockGroupModel,
              sourceDraft?.modelIdentity,
            ),
            lockGroupModel ? sourceDraft?.modelWardrobe : undefined,
          ),
          referenceImages,
        ),
        dashboardApi.generateImages,
      );
      setDrafts((current) =>
        current.map((draft) =>
          draft.id === draftId
            ? {
                ...draft,
                imageSlots: draft.imageSlots.map((value) =>
                  value.id === slotId
                    ? {
                        ...value,
                        status: "ready",
                        image,
                        error: null,
                        generationStartedAt: null,
                      }
                    : value,
                ),
              }
            : draft,
        ),
      );
      const otherIncomplete = drafts.some((draft) =>
        draft.imageSlots.some(
          (value) => value.id !== slotId && value.status !== "ready",
        ),
      );
      setReviewRunPhase(otherIncomplete ? "partial" : "completed");
    } catch (error) {
      const message = error instanceof Error ? error.message : "图片生成失败";
      setImageError(message);
      setReviewRunPhase(reviewSlotSummary.ready ? "partial" : "failed");
      setDrafts((current) =>
        current.map((draft) =>
          draft.id === draftId
            ? {
                ...draft,
                imageSlots: draft.imageSlots.map((value) =>
                  value.id === slotId
                    ? {
                        ...value,
                        status: "error",
                        error: message,
                        generationStartedAt: null,
                      }
                    : value,
                ),
              }
            : draft,
        ),
      );
    } finally {
      void queryClient.invalidateQueries({
        queryKey: ["generated-image-archive"],
      });
    }
  };
  const runBulkAction = async (
    action: "save" | "download",
    draftIds = selectedDraftIds,
  ) => {
    const chosen = drafts.filter((draft) => draftIds.includes(draft.id));
    if (!chosen.length) return;
    setBulkAction(action);
    setImageError("");
    try {
      if (action === "download") {
        const groups: ReviewArchiveGroup[] = chosen.map((draft) => ({
          id: draft.id,
          productName: productName.trim() || "未命名产品",
          reviewText: draft.text,
          savedAt: draft.createdAt,
          directions: directionItems
            .filter((item) => draft.directions.includes(item.key))
            .map((item) => item.title),
          images: draft.imageSlots.flatMap((slot) =>
            slot.image ? [slot.image] : [],
          ),
        }));
        const archive = await createReviewArchive(groups);
        triggerReviewArchiveDownload(archive, reviewArchiveFileName(groups));
      } else {
        for (const draft of chosen) {
          const existing = reviewLibraryQuery.data?.find(
            (item) => item.sourceDraftId === draft.id,
          );
          if (!existing) await saveReviewToLibrary.mutateAsync(draft);
        }
      }
      showImageNotice(
        action === "save"
          ? `已保存 ${chosen.length} 组`
          : `已打包下载 ${chosen.length} 组图文原稿`,
      );
    } catch (error) {
      setImageError(error instanceof Error ? error.message : "批量操作失败");
    } finally {
      setBulkAction(null);
    }
  };
  const startReviewGeneration = async (mode: "new" | "resume") => {
    if (!selectedReferenceAssets.length) {
      setImageError("请先选择至少 1 张产品图作为 GPT Image 2 参考图");
      return;
    }
    if (selectedDirections.length > reviewImageCount) {
      setImageError(
        `${reviewImageCount} 张图最多选择 ${reviewImageCount} 个创作方向`,
      );
      return;
    }
    if (requireConfiguredImageProvider()) return;
    generationAbortControllersRef.current.forEach((controller) =>
      controller.abort(new DOMException("任务已停止", "AbortError")),
    );
    generationAbortControllersRef.current.clear();
    generationRunRef.current += 1;
    generationPausedRef.current = false;
    setGenerationPaused(false);
    setGenerationManuallyPaused(false);
    setImageError("");
    const preparedDrafts =
      mode === "new"
        ? await createReviewDrafts()
        : prepareReviewDraftsForManualContinuation(drafts);
    if (mode === "resume") setDrafts(preparedDrafts);
    const total = preparedDrafts.reduce(
      (count, draft) =>
        count +
        draft.imageSlots.filter((slot) => slot.status !== "ready").length,
      0,
    );
    setReviewProgress({
      completed: 0,
      total,
      active: 0,
      waiting: total,
      failed: 0,
      concurrency: 0,
      startedAt: Date.now(),
    });
    setProgressClock(Date.now());
    setReviewRunPhase("preflight");
    reviewGeneration.mutate({
      preparedDrafts,
      runId: generationRunRef.current,
    });
  };
  const pauseReviewGeneration = () => {
    generationPausedRef.current = true;
    setGenerationPaused(true);
    setReviewRunPhase("paused");
  };
  const continueReviewGeneration = () => {
    generationPausedRef.current = false;
    setGenerationPaused(false);
    if (reviewGeneration.isPending) setReviewRunPhase("running");
    else startReviewGeneration("resume");
  };
  const continueManuallyPausedReviewGeneration = () =>
    startReviewGeneration("resume");
  const pauseActiveReviewGeneration = () => {
    const ready = reviewSlotSummary.ready;
    generationRunRef.current += 1;
    generationPausedRef.current = false;
    setGenerationPaused(false);
    setGenerationManuallyPaused(true);
    setReviewRunPhase("paused");
    generationAbortControllersRef.current.forEach((controller) =>
      controller.abort(
        new DOMException(reviewGenerationPausedMessage, "AbortError"),
      ),
    );
    generationAbortControllersRef.current.clear();
    setDrafts((current) => pauseReviewDraftGeneration(current));
    setReviewProgress((current) => ({
      ...current,
      active: 0,
      waiting: Math.max(0, current.total - current.completed),
      concurrency: 0,
    }));
    showImageNotice(
      ready > 0 ? `已暂停，保留 ${ready} 张已完成图片` : "已暂停生成",
    );
  };
  const stopReviewGeneration = () => {
    const completed = reviewProgress.completed;
    const total = reviewProgress.total;
    generationRunRef.current += 1;
    generationPausedRef.current = false;
    generationAbortControllersRef.current.forEach((controller) =>
      controller.abort(new DOMException("任务已停止", "AbortError")),
    );
    generationAbortControllersRef.current.clear();
    setGenerationPaused(false);
    setGenerationManuallyPaused(false);
    setReviewRunPhase("stopped");
    setDrafts((current) =>
      current.map((draft) => ({
        ...draft,
        imageSlots: draft.imageSlots.map((slot) =>
          slot.status === "generating" || slot.status === "waiting"
            ? {
                ...slot,
                status: "waiting",
                error: reviewGenerationStoppedMessage,
                generationStartedAt: null,
              }
            : slot,
        ),
      })),
    );
    setReviewProgress((current) => ({
      ...current,
      active: 0,
      waiting: Math.max(0, total - completed),
      concurrency: 0,
    }));
    showImageNotice(
      completed > 0
        ? `已停止，保留 ${completed} 张已完成图片`
        : "已停止本批任务",
    );
  };
  const hasGeneratingReviewSlot = drafts.some((draft) =>
    draft.imageSlots.some((slot) => slot.status === "generating"),
  );
  useEffect(() => {
    if (!reviewGeneration.isPending && !hasGeneratingReviewSlot) return;
    setProgressClock(Date.now());
    const timer = window.setInterval(() => setProgressClock(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [hasGeneratingReviewSlot, reviewGeneration.isPending]);
  const runImageGeneration = () => {
    if (!productName.trim()) {
      setImageError("请先填写产品名称");
      setBranch("reviews");
      return;
    }
    if (!selectedReferenceAssets.length) {
      setImageError("请先上传至少 1 张清晰的产品参考图，避免产品结构生成错误");
      setBranch("reviews");
      return;
    }
    if (requireConfiguredImageProvider()) return;
    setImageError("");
    imageGeneration.mutate();
  };
  const downloadGeneratedImage = async (url: string, index: number) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("download");
      const objectUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${productName.trim() || "商品图片"}-${index + 1}.png`;
      link.click();
      URL.revokeObjectURL(objectUrl);
      showImageNotice("图片已下载");
    } catch {
      setImageError("图片下载失败，请稍后重试");
    }
  };
  const providerStatusText = providersQuery.isPending
    ? "读取配置中"
    : providersQuery.isError
      ? "读取失败"
      : configuredProvider?.status === "connected"
        ? "连接正常"
        : configuredProvider?.status === "error"
          ? "连接异常"
          : configuredProvider?.hasApiKey
            ? "等待检测"
            : "需填写 API Key";
  const reviewElapsedSeconds = reviewProgress.startedAt
    ? Math.max(
        0,
        Math.floor((progressClock - reviewProgress.startedAt) / 1_000),
      )
    : 0;
  const reviewElapsedText = `${Math.floor(reviewElapsedSeconds / 60)}:${String(
    reviewElapsedSeconds % 60,
  ).padStart(2, "0")}`;
  const hasIncompleteReviewImages =
    reviewSlotSummary.total > 0 && reviewSlotSummary.remaining > 0;
  const firstReviewSlotError = drafts
    .flatMap((draft) => draft.imageSlots)
    .find((slot) => slot.error)?.error;
  const reviewFailureDetails = reviewImageFailureDetails(drafts);
  return (
    <>
      <div className="page-heading review-studio-heading">
        <div>
          <span>内容资产</span>
          <h2>评价内容工作台</h2>
          <p>产品卖点、图片创作与真实体验草稿统一管理</p>
        </div>
        <button
          className="review-reference-button"
          onClick={() => setReferenceOpen(true)}
        >
          <Link2 />
          同行参考链接{references.length > 0 && <b>{references.length}</b>}
        </button>
      </div>
      <section className="review-branches">
        {branchItems.map((item) => (
          <button
            key={item.key}
            className={branch === item.key ? "active" : ""}
            onClick={() => setBranch(item.key)}
          >
            <item.icon />
            <span>
              <strong>{item.title}</strong>
              <small>{item.detail}</small>
            </span>
          </button>
        ))}
      </section>

      {(reviewGeneration.isPending ||
        generationPaused ||
        generationManuallyPaused) &&
        reviewProgress.total > 0 && (
          <section className="review-task-progress" aria-live="polite">
            <div className="review-task-progress-copy">
              <div>
                <span>
                  {reviewRunPhase === "preflight"
                    ? "正在检测 GPT Image 2 通道"
                    : generationPaused || generationManuallyPaused
                      ? "已暂停新任务"
                      : "GPT Image 2 生成中"}
                </span>
                <strong>
                  {reviewProgress.completed}/{reviewProgress.total}
                </strong>
              </div>
              <small>
                {reviewRunPhase === "preflight"
                  ? "检测通过后自动开始，本阶段不会发起生图"
                  : `处理中 ${reviewProgress.active} · 等待 ${reviewProgress.waiting} · 并发 ${reviewProgress.concurrency} · 已用时 ${reviewElapsedText}`}
              </small>
            </div>
            <progress
              value={reviewProgress.completed}
              max={reviewProgress.total}
            />
            <div className="review-task-progress-actions">
              {reviewGeneration.isPending &&
              !generationPaused &&
              !generationManuallyPaused ? (
                <button type="button" onClick={pauseReviewGeneration}>
                  <Pause />
                  暂停排队
                </button>
              ) : (
                <button
                  type="button"
                  onClick={
                    generationManuallyPaused
                      ? continueManuallyPausedReviewGeneration
                      : continueReviewGeneration
                  }
                >
                  <Play />
                  继续
                </button>
              )}
              <button
                type="button"
                className="review-task-stop"
                onClick={stopReviewGeneration}
              >
                <Square />
                停止
              </button>
            </div>
          </section>
        )}

      {branch === "analysis" && (
        <section className="review-analysis-layout">
          <article className="panel review-analysis-input">
            <header>
              <div>
                <h2>产品资料</h2>
                <p>卖点整理输入</p>
              </div>
            </header>
            <div className="review-analysis-form">
              <label>
                产品名称
                <input
                  value={productName}
                  onChange={(event) => setProductName(event.target.value)}
                  placeholder="输入产品名称"
                />
              </label>
              <label>
                产品品类
                <select
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                >
                  <option>美妆个护</option>
                  <option>母婴用品</option>
                  <option>家清日用</option>
                  <option>食品饮料</option>
                  <option>其他</option>
                </select>
              </label>
              <label>
                品牌名称
                <input
                  value={brandName}
                  maxLength={80}
                  onChange={(event) => setBrandName(event.target.value)}
                  placeholder="例如：简泽主义"
                />
              </label>
              <label>
                目标人群
                <input
                  value={audience}
                  onChange={(event) => setAudience(event.target.value)}
                  placeholder="例如：油皮、通勤女性、宝妈"
                />
              </label>
              <label>
                已确认卖点
                <textarea
                  value={benefits}
                  onChange={(event) => setBenefits(event.target.value)}
                  placeholder="用逗号分隔产品真实卖点"
                />
              </label>
            </div>
          </article>
          <article className="panel review-analysis-result">
            <header>
              <div>
                <h2>卖点结构</h2>
                <p>逐条整理后直接用于生成</p>
              </div>
            </header>
            <div className="selling-point-grid">
              <div>
                <span>目标人群</span>
                <strong>{audience || "待补充"}</strong>
              </div>
              <div>
                <span>核心品类</span>
                <strong>{category}</strong>
              </div>
              <div>
                <span>品牌名称</span>
                <strong>{brandName || "待补充"}</strong>
              </div>
              <div>
                <span>购买动机</span>
                <strong>{sellingPoints[0] ?? "待补充真实卖点"}</strong>
              </div>
              <div>
                <span>体验证据</span>
                <strong>
                  {sellingPoints.slice(1).join("、") || "待补充可见体验"}
                </strong>
              </div>
            </div>
            <div className="selling-point-list">
              {sellingPoints.length ? (
                sellingPoints.map((point, index) => (
                  <div key={`${index}-${point}`}>
                    <b>{String(index + 1).padStart(2, "0")}</b>
                    <input
                      value={point}
                      aria-label={`卖点 ${index + 1}`}
                      onChange={(event) =>
                        updateSellingPoint(index, event.target.value)
                      }
                    />
                    <button
                      type="button"
                      title="删除这条卖点"
                      onClick={() => removeSellingPoint(index)}
                    >
                      <X />
                    </button>
                  </div>
                ))
              ) : (
                <p>暂无卖点数据</p>
              )}
              <button
                type="button"
                className="selling-point-add"
                onClick={addSellingPoint}
              >
                <Plus />
                新增卖点
              </button>
            </div>
          </article>
        </section>
      )}

      {branch === "reviews" && (
        <section className="review-generator">
          <aside className="review-generator-form">
            <div className="review-step">
              <b>01</b>
              <span>
                <strong>产品与人群</strong>
                <small>只使用已确认的真实信息</small>
              </span>
            </div>
            <div className="review-field-grid">
              <label>
                产品名称
                <input
                  value={productName}
                  onChange={(event) => setProductName(event.target.value)}
                  placeholder="例如：氨基酸洁面乳"
                />
              </label>
              <label>
                品类
                <select
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                >
                  <option>美妆个护</option>
                  <option>母婴用品</option>
                  <option>家清日用</option>
                  <option>食品饮料</option>
                  <option>其他</option>
                </select>
              </label>
            </div>
            <label className="review-field">
              品牌名称
              <input
                value={brandName}
                maxLength={80}
                onChange={(event) => setBrandName(event.target.value)}
                placeholder="例如：简泽主义"
              />
            </label>
            <label className="review-field">
              目标人群
              <input
                value={audience}
                onChange={(event) => setAudience(event.target.value)}
                placeholder="例如：敏感肌、通勤女性"
              />
            </label>
            <label className="review-field">
              真实卖点
              <textarea
                value={benefits}
                maxLength={300}
                onChange={(event) => setBenefits(event.target.value)}
                placeholder="例如：泡沫细腻、洗后清爽、按压泵使用方便"
              />
            </label>
            <div className="competitor-review-source">
              <label>
                {publicProductReviewTitle}
                <small>{publicProductReviewDescription}</small>
                <div>
                  <Link2 />
                  <input
                    type="url"
                    value={competitorUrl}
                    maxLength={1000}
                    onChange={(event) => {
                      setCompetitorUrl(event.target.value);
                      setPublicProductReviewAnalysis(null);
                      setPublicProductReviewError("");
                    }}
                    placeholder="https://mobile.yangkeduo.com/goods2.html?..."
                    aria-label="拼多多商品链接"
                  />
                  <button
                    type="button"
                    disabled={
                      publicProductReviewMutation.isPending ||
                      !competitorUrl.trim()
                    }
                    onClick={() => {
                      setPublicProductReviewError("");
                      publicProductReviewMutation.mutate(competitorUrl.trim());
                    }}
                  >
                    {publicProductReviewMutation.isPending ? (
                      <RefreshCw className="spinning" />
                    ) : (
                      <FileSearch />
                    )}
                    {publicProductReviewMutation.isPending
                      ? "抓取中"
                      : "抓取分析"}
                  </button>
                </div>
              </label>
              {publicProductReviewError && (
                <p className="error">
                  <X />
                  {publicProductReviewError}
                </p>
              )}
              {publicProductReviewAnalysis && (
                <div className="competitor-theme-list">
                  <span>
                    商品 ID {publicProductReviewAnalysis.goodsId} · {publicProductReviewAnalysis.message}
                  </span>
                  {publicProductReviewAnalysis.themes.map((theme) => (
                    <b key={theme.name}>
                      {theme.name}
                      <small>{theme.mentions}</small>
                    </b>
                  ))}
                </div>
              )}
            </div>
            <div className="competitor-review-source authorized-review-source">
              <label>
                {authorizedShopReviewTitle}
                <small>{authorizedShopReviewDescription}</small>
                <div>
                  <Database />
                  <span>
                    {competitorAnalysis
                      ? `${competitorAnalysis.shopName} · mallId ${competitorAnalysis.mallId}`
                      : "店铺身份将在读取前自动校验"}
                  </span>
                  <button
                    type="button"
                    disabled={competitorReviewAnalysis.isPending}
                    onClick={() => {
                      setCompetitorError("");
                      competitorReviewAnalysis.mutate();
                    }}
                  >
                    {competitorReviewAnalysis.isPending ? (
                      <RefreshCw className="spinning" />
                    ) : (
                      <FileSearch />
                    )}
                    {competitorReviewAnalysis.isPending ? "读取中" : "读取并分析"}
                  </button>
                </div>
              </label>
              {competitorError && (
                <p className="error">
                  <X />
                  {competitorError}
                </p>
              )}
              {competitorAnalysis && (
                <div className="competitor-theme-list">
                  <span>{competitorAnalysis.message}</span>
                  {competitorAnalysis.themes.map((theme) => (
                    <b key={theme.name}>
                      {theme.name}
                      <small>{theme.mentions}</small>
                    </b>
                  ))}
                </div>
              )}
            </div>
            <div className="review-step">
              <b>02</b>
              <span>
                <strong>粘贴产品素材</strong>
                <small>至少 1 张；可按实际情况添加正面、背面与细节图</small>
              </span>
            </div>
            <div
              className="product-upload"
              tabIndex={0}
              onPaste={handlePaste}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                void addFiles(event.dataTransfer.files);
              }}
            >
              <input
                ref={inputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => {
                  if (event.target.files) void addFiles(event.target.files);
                  event.target.value = "";
                }}
              />
              <button
                type="button"
                className="product-paste-button"
                onClick={() => void pasteFromClipboard()}
              >
                <ClipboardPaste />
                <span>
                  <strong>从剪贴板粘贴图片</strong>
                  <small>
                    {pasteMessage || "支持网页复制、截图与本地图片 · 最多 8 张"}
                  </small>
                </span>
              </button>
              <button
                type="button"
                className="product-file-button"
                title="选择本地图片"
                onClick={() => inputRef.current?.click()}
              >
                <FolderOpen />
              </button>
            </div>
            {assets.length > 0 && (
              <div className="product-asset-section">
                <div className="product-reference-summary">
                  <span>
                    GPT Image 2 产品参考图 · 已选{" "}
                    {selectedReferenceAssets.length} 张
                  </span>
                  <small>
                    至少选择 1 张，可多选并调整顺序；未勾选图片仅保留为素材
                  </small>
                </div>
                <div className="product-assets">
                  {assets.map((asset, index) => {
                    const referenceIndex = referenceAssetIds.indexOf(asset.id);
                    const isReference = referenceIndex >= 0;
                    return (
                      <div
                        key={asset.id}
                        className={isReference ? "reference-selected" : ""}
                      >
                        <button
                          type="button"
                          className="product-asset-preview"
                          title="查看大图"
                          aria-label={`查看产品素材 ${index + 1}`}
                          onClick={() =>
                            setPreviewImage({
                              gallery: assets,
                              title: productName.trim()
                                ? `${productName.trim()} · 产品素材`
                                : "产品素材",
                              index,
                            })
                          }
                        >
                          <img src={asset.url} alt={asset.name} />
                          <span aria-hidden="true">
                            <ZoomIn />
                          </span>
                        </button>
                        <button
                          type="button"
                          className="product-asset-reference"
                          title={isReference ? "取消参考图" : "选为参考图"}
                          aria-label={isReference ? "取消参考图" : "选为参考图"}
                          onClick={() => toggleReferenceAsset(asset.id)}
                        >
                          {isReference ? <CheckSquare /> : <Square />}
                        </button>
                        {isReference && (
                          <>
                            <b className="product-reference-order">
                              {referenceIndex + 1}
                            </b>
                            <div className="product-reference-move">
                              <button
                                type="button"
                                title="参考顺序前移"
                                disabled={referenceIndex === 0}
                                onClick={() => moveReferenceAsset(asset.id, -1)}
                              >
                                <ChevronLeft />
                              </button>
                              <button
                                type="button"
                                title="参考顺序后移"
                                disabled={
                                  referenceIndex ===
                                  referenceAssetIds.length - 1
                                }
                                onClick={() => moveReferenceAsset(asset.id, 1)}
                              >
                                <ChevronRight />
                              </button>
                            </div>
                          </>
                        )}
                        <button
                          type="button"
                          className="product-asset-remove"
                          title="删除"
                          aria-label={`删除产品素材 ${index + 1}`}
                          onClick={() => removeAsset(asset.id)}
                        >
                          <X />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="review-step">
              <b>03</b>
              <span>
                <strong>图片生成方向</strong>
                <small>可多选，每条评价按方向组合</small>
              </span>
            </div>
            <div className="direction-grid">
              {directionItems.map((item) => (
                <button
                  type="button"
                  key={item.key}
                  className={
                    selectedDirections.includes(item.key) ? "selected" : ""
                  }
                  onClick={() => {
                    if (selectedDirections.includes(item.key)) {
                      setSelectedDirections((current) =>
                        current.filter((key) => key !== item.key),
                      );
                      return;
                    }
                    if (selectedDirections.length >= reviewImageCount) {
                      setImageError(
                        `当前每组 ${reviewImageCount} 张图，最多选择 ${reviewImageCount} 个方向`,
                      );
                      return;
                    }
                    setImageError("");
                    setSelectedDirections((current) => [...current, item.key]);
                  }}
                >
                  <item.icon />
                  <span>
                    <strong>{item.title}</strong>
                    <small>{item.detail}</small>
                  </span>
                  <span
                    className="direction-edit"
                    role="button"
                    tabIndex={0}
                    title={`编辑${item.title}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setEditingDirection(item.key);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        setEditingDirection(item.key);
                      }
                    }}
                  >
                    <Pencil />
                  </span>
                  {selectedDirections.includes(item.key) && <Check />}
                </button>
              ))}
            </div>
            {editingDirection && (
              <section className="direction-requirement-editor">
                <header>
                  <strong>编辑图片方向</strong>
                  <button
                    type="button"
                    title="关闭"
                    onClick={() => setEditingDirection(null)}
                  >
                    <X />
                  </button>
                </header>
                <label>
                  方向名称
                  <input
                    value={directionContent[editingDirection].title}
                    maxLength={16}
                    onChange={(event) =>
                      setDirectionContent((current) => ({
                        ...current,
                        [editingDirection]: {
                          ...current[editingDirection],
                          title: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
                <label>
                  卡片说明
                  <input
                    value={directionContent[editingDirection].detail}
                    maxLength={40}
                    onChange={(event) =>
                      setDirectionContent((current) => ({
                        ...current,
                        [editingDirection]: {
                          ...current[editingDirection],
                          detail: event.target.value,
                        },
                      }))
                    }
                  />
                </label>
                <label>
                  详细生成要求
                  <textarea
                    value={directionRequirements[editingDirection]}
                    maxLength={300}
                    onChange={(event) =>
                      setDirectionRequirements((current) => ({
                        ...current,
                        [editingDirection]: event.target.value,
                      }))
                    }
                  />
                </label>
                <footer>
                  <span>
                    {directionRequirements[editingDirection].length}/300
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditingDirection(null)}
                  >
                    <Check />
                    完成
                  </button>
                </footer>
              </section>
            )}
            <label className="review-generation-requirements">
              <span>
                <strong>通用写实要求</strong>
                <small>{generationRequirements.length}/500</small>
              </span>
              <textarea
                value={generationRequirements}
                maxLength={500}
                onChange={(event) =>
                  setGenerationRequirements(event.target.value)
                }
                placeholder={defaultGenerationRequirements}
              />
            </label>
            <div className="review-model-lock-row">
              <label className="review-model-lock">
                <input
                  type="checkbox"
                  checked={lockGroupModel}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    setLockGroupModel(enabled);
                    if (enabled) {
                      setDrafts((current) =>
                        ensureReviewGroupModelIdentities(current),
                      );
                    }
                  }}
                />
                <span>
                  <strong>同组锁定同一模特与穿搭</strong>
                  <small>
                    同组人物、衣服和身体特征一致，不同组自动更换
                  </small>
                </span>
              </label>
              <button
                type="button"
                className="model-template-open"
                onClick={() => setModelTemplateOpen(true)}
              >
                <Eye />
                查看人物模板池
              </button>
            </div>
            <label className="review-text-requirements">
              <span>
                <strong>评语生成方向</strong>
                <small>{reviewRequirements.length}/500</small>
              </span>
              <textarea
                value={reviewRequirements}
                maxLength={500}
                onChange={(event) => setReviewRequirements(event.target.value)}
                placeholder={defaultReviewRequirements}
              />
            </label>
            <section className="review-endorsement-settings">
              <header>
                <span>
                  <strong>模拟背书设定</strong>
                  <small>仅用于内部草稿</small>
                </span>
                <em>虚构设定</em>
              </header>
              <div className="review-endorsement-options">
                <label>
                  <input
                    type="checkbox"
                    checked={endorsementSettings.mentionEmbassy}
                    onChange={(event) =>
                      setEndorsementSettings((current) => ({
                        ...current,
                        mentionEmbassy: event.target.checked,
                      }))
                    }
                  />
                  <span>
                    <strong>加入使馆相关机构人员使用设定</strong>
                    <small>只在少量评语里穿插具体见闻，不会每条重复</small>
                  </span>
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={endorsementSettings.mentionCelebrity}
                    onChange={(event) =>
                      setEndorsementSettings((current) => ({
                        ...current,
                        mentionCelebrity: event.target.checked,
                      }))
                    }
                  />
                  <span>
                    <strong>加入对应国家虚构名人使用设定</strong>
                    <small>按后台洗护、巡演随身包等具体场景轮换</small>
                  </span>
                </label>
              </div>
              <div className="review-endorsement-countries">
                <span>品牌国家</span>
                <div>
                  {(["英国", "日本", "韩国"] as const).map((country) => (
                    <button
                      type="button"
                      key={country}
                      className={
                        endorsementSettings.country === country ? "active" : ""
                      }
                      disabled={
                        !endorsementSettings.mentionEmbassy &&
                        !endorsementSettings.mentionCelebrity
                      }
                      onClick={() =>
                        setEndorsementSettings((current) => ({
                          ...current,
                          country,
                        }))
                      }
                    >
                      {country}
                    </button>
                  ))}
                </div>
              </div>
              <p>
                启用后约三分之一评语穿插一种设定；两项同时开启会轮换，不叠在同一句。带设定的评语保留“内部模拟｜虚构设定｜禁止直接发布”标记。
              </p>
            </section>
            <div className="review-step">
              <b>04</b>
              <span>
                <strong>输出设置</strong>
                <small>每条评价对应一组图片</small>
              </span>
            </div>
            <div className="review-settings">
              <label>
                评价数量
                <div>
                  <button
                    onClick={() =>
                      setReviewCount((value) => Math.max(1, value - 1))
                    }
                  >
                    −
                  </button>
                  <input
                    aria-label="评价数量"
                    className="review-count-input"
                    inputMode="numeric"
                    max={20}
                    min={1}
                    step={1}
                    type="number"
                    value={reviewCountInput}
                    onBlur={(event) => {
                      const next = normalizeReviewCountInput(
                        event.currentTarget.value,
                        reviewCount,
                      );
                      setReviewCount(next);
                      setReviewCountInput(String(next));
                    }}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      setReviewCountInput(value);
                      const next = Number(value);
                      if (
                        value !== "" &&
                        Number.isInteger(next) &&
                        next >= 1 &&
                        next <= 20
                      ) {
                        setReviewCount(next);
                      }
                    }}
                    onFocus={(event) => event.currentTarget.select()}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.currentTarget.blur();
                    }}
                  />
                  <button
                    onClick={() =>
                      setReviewCount((value) => Math.min(20, value + 1))
                    }
                  >
                    ＋
                  </button>
                </div>
              </label>
              <label>
                每组图片
                <div className="image-count-options">
                  {[3, 4, 5].map((value) => (
                    <button
                      key={value}
                      className={reviewImageCount === value ? "active" : ""}
                      onClick={() => {
                        setReviewImageCount(value);
                        setSelectedDirections((current) =>
                          current.slice(0, value),
                        );
                      }}
                    >
                      {value} 张
                    </button>
                  ))}
                </div>
              </label>
            </div>
            <label className="review-authorization">
              <input
                type="checkbox"
                checked={authorized}
                onChange={(event) => setAuthorized(event.target.checked)}
              />
              <span>产品素材和体验信息已获授权，生成内容需确认后使用</span>
            </label>
            <div className="review-generation-actions">
              <button
                className="review-generate"
                disabled={
                  !authorized ||
                  !productName.trim() ||
                  selectedDirections.length === 0 ||
                  generationManuallyPaused ||
                  reviewGeneration.isPending
                }
                onClick={() => {
                  setImageError("");
                  startReviewGeneration(
                    hasIncompleteReviewImages ? "resume" : "new",
                  );
                }}
              >
                {generationManuallyPaused ? (
                  <Pause />
                ) : reviewGeneration.isPending ? (
                  <RefreshCw className="spinning" />
                ) : (
                  <WandSparkles />
                )}
                {generationManuallyPaused
                  ? `已暂停 ${reviewSlotSummary.ready}/${reviewSlotSummary.total}`
                  : reviewRunPhase === "preflight"
                    ? "正在检测生图通道"
                    : reviewGeneration.isPending
                      ? generationPaused
                        ? `已暂停 ${reviewSlotSummary.ready}/${reviewSlotSummary.total}`
                        : `正在生成 ${reviewSlotSummary.ready}/${reviewSlotSummary.total}`
                      : hasIncompleteReviewImages
                        ? `继续剩余 ${reviewSlotSummary.remaining} 张`
                        : `生成 ${reviewCount} 组图文 · ${reviewCount * reviewImageCount} 张图`}
              </button>
              {reviewGeneration.isPending && !generationManuallyPaused && (
                <button
                  type="button"
                  className="review-pause"
                  onClick={pauseActiveReviewGeneration}
                >
                  <Pause />
                  暂停生成
                </button>
              )}
              {generationManuallyPaused && (
                <div className="review-paused-actions">
                  <button
                    type="button"
                    className="review-continue"
                    disabled={
                      !canStartPausedReviewGenerationAction(
                        reviewGeneration.isPending,
                        generationManuallyPaused,
                      )
                    }
                    onClick={continueManuallyPausedReviewGeneration}
                  >
                    <Play />
                    继续生成
                  </button>
                  <button
                    type="button"
                    className="review-regenerate"
                    title="从第一张开始生成一批新图"
                    disabled={
                      !canStartPausedReviewGenerationAction(
                        reviewGeneration.isPending,
                        generationManuallyPaused,
                      )
                    }
                    onClick={() => startReviewGeneration("new")}
                  >
                    <RefreshCw />
                    重新生成
                  </button>
                </div>
              )}
            </div>
            {imageError && (
              <div className="review-image-error">
                <X />
                {imageError}
              </div>
            )}
          </aside>
          <div className="review-results">
            <header>
              <div>
                <h3>生成结果</h3>
                <p>
                  {drafts.length
                    ? `${drafts.length} 组草稿 · ${drafts.flatMap((draft) => draft.imageSlots).filter((slot) => slot.status === "ready").length}/${drafts.flatMap((draft) => draft.imageSlots).length} 张已生成`
                    : "生成后可逐组确认、复制与保存"}
                </p>
              </div>
              {drafts.length > 0 && (
                <button
                  disabled={reviewGeneration.isPending}
                  onClick={() => {
                    setDrafts([]);
                    setSelectedDraftIds([]);
                    setGenerationPaused(false);
                    setGenerationManuallyPaused(false);
                    generationPausedRef.current = false;
                    setReviewRunPhase("idle");
                    setImageError("");
                    setReviewProgress({
                      completed: 0,
                      total: 0,
                      active: 0,
                      waiting: 0,
                      failed: 0,
                      concurrency: 0,
                      startedAt: 0,
                    });
                  }}
                >
                  <Trash2 />
                  清空
                </button>
              )}
            </header>
            {drafts.length > 0 && (
              <div
                className={`review-run-status ${reviewRunPhase}`}
                aria-live="polite"
              >
                {reviewRunPhase === "preflight" ||
                reviewRunPhase === "running" ? (
                  <RefreshCw className="spinning" />
                ) : reviewRunPhase === "completed" ? (
                  <Check />
                ) : reviewRunPhase === "failed" ||
                  reviewRunPhase === "partial" ? (
                  <X />
                ) : reviewRunPhase === "stopped" ? (
                  <Square />
                ) : (
                  <Pause />
                )}
                <span>
                  <strong>
                    {reviewRunPhase === "preflight"
                      ? "正在检测生图通道"
                      : reviewRunPhase === "running"
                        ? "图片生成中"
                        : reviewRunPhase === "completed"
                          ? "本批图片已全部完成"
                          : reviewRunPhase === "partial"
                            ? "部分图片未完成"
                            : reviewRunPhase === "failed"
                              ? "本轮未能开始或已经停止"
                              : reviewRunPhase === "stopped"
                                ? "任务已停止"
                                : "任务等待手动继续"}
                  </strong>
                  <small>
                    {reviewRunPhase === "preflight"
                      ? "检测通过后自动开始，本阶段不会发起生图"
                      : reviewRunPhase === "running"
                        ? `${reviewSlotSummary.ready}/${reviewSlotSummary.total} 张已生成，${reviewSlotSummary.failed} 张生成失败`
                        : reviewRunPhase === "completed"
                          ? `${reviewSlotSummary.total} 张均已生成`
                          : firstReviewSlotError ||
                            `${reviewSlotSummary.remaining} 张等待继续`}
                  </small>
                </span>
                {!reviewGeneration.isPending && hasIncompleteReviewImages && (
                  <button
                    type="button"
                    onClick={() => startReviewGeneration("resume")}
                  >
                    <Play />
                    继续剩余 {reviewSlotSummary.remaining} 张
                  </button>
                )}
              </div>
            )}
            {reviewFailureDetails.length > 0 && (
              <section
                className="review-failure-details"
                aria-label="生图失败明细"
              >
                <header>
                  <span>
                    <X />
                    实际错误明细
                  </span>
                  <small>{reviewFailureDetails.length} 个图片位</small>
                </header>
                <ol>
                  {reviewFailureDetails.map((failure) => {
                    const directionTitle = directionItems.find(
                      (item) => item.key === failure.direction,
                    )?.title;
                    return (
                      <li key={`${failure.draftId}-${failure.slotId}`}>
                        <strong>
                          评价 {String(failure.draftIndex + 1).padStart(2, "0")}{" "}
                          · {directionTitle ?? failure.direction}
                        </strong>
                        <span>{failure.message}</span>
                      </li>
                    );
                  })}
                </ol>
              </section>
            )}
            {drafts.length > 0 && (
              <div className="review-bulk-toolbar">
                <button
                  type="button"
                  onClick={() =>
                    setSelectedDraftIds(
                      selectedDraftIds.length === drafts.length
                        ? []
                        : drafts.map((draft) => draft.id),
                    )
                  }
                >
                  {selectedDraftIds.length === drafts.length ? (
                    <CheckSquare />
                  ) : (
                    <Square />
                  )}
                  {selectedDraftIds.length
                    ? `已选 ${selectedDraftIds.length} 组`
                    : "全选"}
                </button>
                <span />
                <button
                  type="button"
                  disabled={!selectedDraftIds.length || bulkAction !== null}
                  onClick={() => void runBulkAction("save")}
                >
                  {bulkAction === "save" ? (
                    <RefreshCw className="spinning" />
                  ) : (
                    <Save />
                  )}
                  批量保存
                </button>
                <button
                  type="button"
                  disabled={!selectedDraftIds.length || bulkAction !== null}
                  onClick={() => void runBulkAction("download")}
                >
                  {bulkAction === "download" ? (
                    <RefreshCw className="spinning" />
                  ) : (
                    <Download />
                  )}
                  下载 ZIP
                </button>
              </div>
            )}
            {drafts.length ? (
              <div className="review-draft-list">
                {drafts.map((draft, draftIndex) => (
                  <article
                    key={draft.id}
                    className={
                      selectedDraftIds.includes(draft.id) ? "selected" : ""
                    }
                  >
                    <header>
                      <button
                        type="button"
                        className="review-draft-selector"
                        onClick={() =>
                          setSelectedDraftIds((current) =>
                            current.includes(draft.id)
                              ? current.filter((id) => id !== draft.id)
                              : [...current, draft.id],
                          )
                        }
                      >
                        {selectedDraftIds.includes(draft.id) ? (
                          <CheckSquare />
                        ) : (
                          <Square />
                        )}
                        评价 {String(draftIndex + 1).padStart(2, "0")}
                      </button>
                      <button onClick={() => void copy(draft)}>
                        {copiedId === draft.id ? <Check /> : <Clipboard />}
                        {copiedId === draft.id ? "已复制" : "复制评语"}
                      </button>
                    </header>
                    <div className="review-image-group">
                      {draft.imageSlots.map((slot, imageIndex) => {
                        const direction = directionItems.find(
                          (item) => item.key === slot.direction,
                        )!;
                        return (
                          <div
                            key={slot.id}
                            className={slot.status}
                            role={slot.image ? "button" : undefined}
                            tabIndex={slot.image ? 0 : undefined}
                            aria-label={
                              slot.image
                                ? `查看${direction.title}原图`
                                : undefined
                            }
                            onClick={() =>
                              slot.image &&
                              setPreviewImage({
                                gallery: draft.imageSlots.flatMap((value) =>
                                  value.image ? [value.image] : [],
                                ),
                                title:
                                  direction.title +
                                  " · 评价 " +
                                  (draftIndex + 1),
                                index: draft.imageSlots
                                  .filter((value) => value.image)
                                  .findIndex((value) => value.id === slot.id),
                              })
                            }
                            onKeyDown={(event) => {
                              if (
                                slot.image &&
                                (event.key === "Enter" || event.key === " ")
                              ) {
                                event.preventDefault();
                                setPreviewImage({
                                  gallery: draft.imageSlots.flatMap((value) =>
                                    value.image ? [value.image] : [],
                                  ),
                                  title:
                                    direction.title +
                                    " · 评价 " +
                                    (draftIndex + 1),
                                  index: draft.imageSlots
                                    .filter((value) => value.image)
                                    .findIndex((value) => value.id === slot.id),
                                });
                              }
                            }}
                          >
                            {slot.image ? (
                              <img
                                src={slot.image.url}
                                alt={`${direction.title}生成图`}
                              />
                            ) : slot.status === "generating" ? (
                              <RefreshCw className="spinning" />
                            ) : slot.status === "error" ? (
                              <X />
                            ) : (
                              <Images />
                            )}
                            <span>{direction.title}</span>
                            {slot.status !== "ready" && (
                              <small>
                                {slot.status === "generating"
                                  ? `${
                                      reviewRunPhase === "preflight"
                                        ? "正在检测通道"
                                        : "正在生成"
                                    } · 已用 ${reviewImageElapsedSeconds(
                                      slot.generationStartedAt,
                                      progressClock,
                                    )} 秒`
                                  : slot.status === "error"
                                    ? slot.error || "生成失败"
                                    : slot.error ||
                                      (reviewRunPhase === "preflight"
                                        ? "等待通道检测"
                                        : reviewRunPhase === "running"
                                          ? "排队中"
                                          : reviewRunPhase === "paused"
                                            ? "已暂停，等待继续"
                                            : reviewRunPhase === "stopped"
                                              ? "已停止，等待继续"
                                              : "等待手动继续")}
                              </small>
                            )}
                            {slot.status === "error" && (
                              <button
                                type="button"
                                className="review-slot-retry"
                                title={slot.error || "重新生成此图"}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void retryReviewSlot(draft.id, slot.id);
                                }}
                              >
                                <RefreshCw />
                                重试
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="review-draft-text-editor">
                      <textarea
                        value={draft.text}
                        aria-label={`评价 ${draftIndex + 1} 文案`}
                        onChange={(event) =>
                          setDrafts((current) =>
                            current.map((item) =>
                              item.id === draft.id
                                ? { ...item, text: event.target.value }
                                : item,
                            ),
                          )
                        }
                      />
                      <button
                        type="button"
                        title="重新生成这条评语"
                        onClick={() => regenerateReviewText(draft.id)}
                      >
                        <RefreshCw />
                        改写评语
                      </button>
                    </div>
                    <footer>
                      <div className="review-draft-save-state">
                        <span>
                          {savedSourceDraftIds.has(draft.id)
                            ? "已入库"
                            : "待确认草稿"}
                        </span>
                        <button
                          type="button"
                          disabled={
                            savedSourceDraftIds.has(draft.id) ||
                            !draft.imageSlots.some((slot) => slot.image) ||
                            (saveReviewToLibrary.isPending &&
                              saveReviewToLibrary.variables?.id === draft.id)
                          }
                          onClick={() => saveReviewToLibrary.mutate(draft)}
                        >
                          {saveReviewToLibrary.isPending &&
                          saveReviewToLibrary.variables?.id === draft.id ? (
                            <RefreshCw className="spinning" />
                          ) : savedSourceDraftIds.has(draft.id) ? (
                            <Check />
                          ) : (
                            <Save />
                          )}
                          {savedSourceDraftIds.has(draft.id)
                            ? "已保存"
                            : "保存到数据库"}
                        </button>
                        <button
                          type="button"
                          disabled={
                            !draft.imageSlots.some((slot) => slot.image)
                          }
                          onClick={() =>
                            void runBulkAction("download", [draft.id])
                          }
                        >
                          <Download />
                          下载 ZIP
                        </button>
                      </div>
                      <small>
                        {directionItems
                          .filter((item) => draft.directions.includes(item.key))
                          .map((item) => item.title)
                          .join(" · ")}
                      </small>
                    </footer>
                  </article>
                ))}
              </div>
            ) : (
              <div className="review-result-empty">
                <Images />
                <strong>等待生成图文草稿</strong>
                <span>填写产品信息、上传图片并选择生成方向</span>
              </div>
            )}
          </div>
        </section>
      )}

      {branch === "images" && (
        <section className="panel image-task-board">
          <header>
            <div>
              <h2>图片创作任务</h2>
              <p>
                {productName.trim()
                  ? `${productName} · 选择创作方向并执行生成`
                  : "产品参考图与六种创作方向"}
              </p>
            </div>
            <button className="primary" onClick={() => setBranch("reviews")}>
              <Plus />
              新建任务
            </button>
          </header>
          <div className="image-direction-board">
            {directionItems.map((item) => (
              <button
                type="button"
                key={item.key}
                className={imageDirection === item.key ? "active" : ""}
                onClick={() => setImageDirection(item.key)}
              >
                <item.icon />
                <span>
                  <strong>{item.title}</strong>
                  <small>{item.detail}</small>
                </span>
                {imageDirection === item.key && <Check />}
              </button>
            ))}
          </div>
          <div className="studio-image-connector">
            <div className="studio-provider-select">
              <span>
                <strong>图片生成渠道</strong>
                <small
                  className={
                    configuredProvider?.status === "connected"
                      ? "connected"
                      : configuredProvider?.status === "error"
                        ? "error"
                        : ""
                  }
                >
                  <i />
                  {providerStatusText}
                </small>
              </span>
              <div>
                <strong className="studio-fixed-provider">GPT Image 2</strong>
                {canManageProvider && (
                  <button
                    type="button"
                    className="studio-provider-settings"
                    title="配置图片生成接口"
                    onClick={() => setProviderOpen(true)}
                  >
                    <Settings2 />
                  </button>
                )}
              </div>
            </div>
            <div className="studio-image-run">
              <label>
                生成数量
                <select
                  value={taskImageCount}
                  onChange={(event) =>
                    setTaskImageCount(Number(event.target.value))
                  }
                >
                  <option value={1}>1 张</option>
                  <option value={2}>2 张</option>
                  <option value={3}>3 张</option>
                  <option value={4}>4 张</option>
                  <option value={5}>5 张</option>
                </select>
              </label>
              <button
                type="button"
                disabled={imageGeneration.isPending}
                onClick={runImageGeneration}
              >
                {imageGeneration.isPending ? (
                  <RefreshCw className="spinning" />
                ) : (
                  <WandSparkles />
                )}
                {imageGeneration.isPending
                  ? "正在生成"
                  : `生成${directionItems.find((item) => item.key === imageDirection)?.title ?? "图片"}`}
              </button>
            </div>
          </div>
          {imageError && (
            <div className="studio-image-error">
              <X />
              {imageError}
            </div>
          )}
          {imageGeneration.isPending ? (
            <div className="image-model-state">
              <RefreshCw className="spinning" />
              <span>
                <strong>正在生成图片</strong>
                <small>{providerLabels[imageProvider]}正在处理当前任务</small>
              </span>
            </div>
          ) : generatedImages.length ? (
            <>
              <div className="studio-generated-actions">
                <span>{generatedImages.length} 张原图已保存</span>
                <button
                  type="button"
                  disabled={
                    saveGeneratedTask.isPending || !generatedImages.length
                  }
                  onClick={() => saveGeneratedTask.mutate()}
                >
                  {saveGeneratedTask.isPending ? (
                    <RefreshCw className="spinning" />
                  ) : (
                    <Save />
                  )}
                  保存到素材数据库
                </button>
              </div>
              <div className="studio-generated-grid">
                {generatedImages.map((image, index) => (
                  <article key={image.id}>
                    <button
                      type="button"
                      className="studio-generated-preview"
                      title="查看生成原图"
                      onClick={() =>
                        setPreviewImage({
                          gallery: generatedImages,
                          title:
                            directionItems.find(
                              (item) => item.key === imageDirection,
                            )?.title ?? "生成原图",
                          index,
                        })
                      }
                    >
                      <img
                        src={image.url}
                        alt={`${productName || "商品"}${directionItems.find((item) => item.key === imageDirection)?.title ?? "图片"} ${index + 1}`}
                      />
                      <ZoomIn />
                    </button>
                    <footer>
                      <button
                        type="button"
                        title="下载图片"
                        onClick={() =>
                          void downloadGeneratedImage(image.url, index)
                        }
                      >
                        <Download />
                      </button>
                    </footer>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <div className="image-model-state">
              <Sparkles />
              <span>
                <strong>等待执行图片任务</strong>
                <small>使用 GPT Image 2 开始生成</small>
              </span>
            </div>
          )}
        </section>
      )}

      {branch === "database" && (
        <section className="panel review-library">
          <header>
            <div>
              <h2>素材数据库</h2>
              <p>已保存的原图与评价文案 · 一键打包到本地</p>
            </div>
            <div className="review-library-tools">
              <button
                type="button"
                className={`review-library-batch-toggle${libraryBatchMode ? " active" : ""}`}
                onClick={() => {
                  setLibraryBatchMode((current) => !current);
                  setSelectedLibraryIds([]);
                }}
              >
                {libraryBatchMode ? <X /> : <CheckSquare />}
                {libraryBatchMode ? "退出管理" : "批量管理"}
              </button>
              {libraryBatchMode && (
                <>
                  <button
                    type="button"
                    className="review-library-select-all"
                    disabled={!filteredLibraryItems.length}
                    onClick={() =>
                      setSelectedLibraryIds((current) =>
                        updateReviewLibrarySelection(
                          current,
                          filteredLibraryItems.map((item) => item.id),
                          !allFilteredLibraryItemsSelected,
                        ),
                      )
                    }
                  >
                    {allFilteredLibraryItemsSelected ? (
                      <CheckSquare />
                    ) : (
                      <Square />
                    )}
                    {allFilteredLibraryItemsSelected ? "取消当前" : "全选当前"}
                  </button>
                  <span className="review-library-selected-count">
                    已选 {selectedLibraryGroups.length} 组
                  </span>
                  <button
                    type="button"
                    className="review-library-archive"
                    disabled={
                      !selectedLibraryGroups.length || archiveAction !== null
                    }
                    onClick={() =>
                      void downloadReviewArchive(
                        selectedLibraryGroups,
                        "library-selected",
                      )
                    }
                  >
                    {archiveAction === "library-selected" ? (
                      <RefreshCw className="spinning" />
                    ) : (
                      <Download />
                    )}
                    {archiveAction === "library-selected"
                      ? "打包中"
                      : "下载所选 ZIP"}
                  </button>
                </>
              )}
              <label>
                <Search />
                <input
                  value={librarySearch}
                  onChange={(event) => setLibrarySearch(event.target.value)}
                  placeholder="搜索产品、评价、标签或日期"
                />
                {librarySearch && (
                  <button
                    type="button"
                    title="清空搜索"
                    onClick={() => setLibrarySearch("")}
                  >
                    <X />
                  </button>
                )}
              </label>
              <span>
                {librarySearch
                  ? `${filteredLibraryItems.length} / ${reviewLibraryQuery.data?.length ?? 0} 条`
                  : `${reviewLibraryQuery.data?.length ?? 0} 条`}
              </span>
              <button
                className="review-library-refresh"
                type="button"
                title="刷新"
                onClick={() => reviewLibraryQuery.refetch()}
              >
                <RefreshCw
                  className={reviewLibraryQuery.isFetching ? "spinning" : ""}
                />
              </button>
            </div>
          </header>
          {reviewLibraryQuery.isLoading ? (
            <div className="review-result-empty">
              <RefreshCw className="spinning" />
              <strong>正在读取数据库</strong>
            </div>
          ) : reviewLibraryQuery.isError ? (
            <div className="review-result-empty">
              <X />
              <strong>数据库读取失败</strong>
              <button onClick={() => reviewLibraryQuery.refetch()}>重试</button>
            </div>
          ) : filteredLibraryItems.length ? (
            <div className="review-library-grid">
              {filteredLibraryItems.map((item) => {
                const isSelected = selectedLibraryIdSet.has(item.id);
                return (
                  <article
                    key={item.id}
                    className={isSelected ? "selected" : undefined}
                  >
                    <header>
                      {libraryBatchMode && (
                        <button
                          type="button"
                          className="review-library-selector"
                          title={isSelected ? "取消选择" : "选择这组评价"}
                          aria-label={isSelected ? "取消选择" : "选择这组评价"}
                          aria-pressed={isSelected}
                          onClick={() =>
                            setSelectedLibraryIds((current) =>
                              updateReviewLibrarySelection(
                                current,
                                [item.id],
                                !isSelected,
                              ),
                            )
                          }
                        >
                          {isSelected ? <CheckSquare /> : <Square />}
                        </button>
                      )}
                      <div>
                        <strong>{item.productName}</strong>
                        <small>
                          {new Date(item.savedAt).toLocaleString("zh-CN")} ·{" "}
                          {item.images.length} 张原图
                        </small>
                      </div>
                    </header>
                    <div className="review-library-images">
                      {item.images.slice(0, 5).map((image, index) => (
                        <div key={image.id}>
                          <button
                            type="button"
                            title="点击放大"
                            onClick={() =>
                              setPreviewImage({
                                gallery: item.images,
                                title: item.productName,
                                index,
                              })
                            }
                          >
                            <img
                              src={image.url}
                              alt={`${item.productName} ${index + 1}`}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                    <p>{item.reviewText}</p>
                    <footer>
                      <span>{item.directions.join(" · ")}</span>
                      <div>
                        <button
                          type="button"
                          className="review-library-archive"
                          disabled={archiveAction !== null}
                          title="将这组原图与评价文案下载为 ZIP"
                          onClick={() =>
                            void downloadReviewArchive(
                              [item],
                              `library-${item.id}`,
                            )
                          }
                        >
                          {archiveAction === `library-${item.id}` ? (
                            <RefreshCw className="spinning" />
                          ) : (
                            <Download />
                          )}
                          {archiveAction === `library-${item.id}`
                            ? "打包中"
                            : "下载 ZIP"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedLibraryId(item.id)}
                        >
                          <Eye />
                          查看
                        </button>
                        <button
                          type="button"
                          title="删除"
                          disabled={deleteReviewFromLibrary.isPending}
                          onClick={() =>
                            deleteReviewFromLibrary.mutate(item.id)
                          }
                        >
                          <Trash2 />
                        </button>
                      </div>
                    </footer>
                  </article>
                );
              })}
            </div>
          ) : librarySearch && reviewLibraryQuery.data?.length ? (
            <div className="review-result-empty">
              <Search />
              <strong>没有找到匹配的评价</strong>
              <span>可搜索产品名称、评价内容、图片方向或保存日期</span>
              <button onClick={() => setLibrarySearch("")}>清空搜索</button>
            </div>
          ) : (
            <div className="review-result-empty">
              <Database />
              <strong>数据库暂无内容</strong>
              <span>在每组评价下方点击保存后显示在这里</span>
            </div>
          )}
        </section>
      )}

      {branch === "history" && (
        <section className="panel review-history">
          <header>
            <div>
              <h2>生成记录</h2>
              <p>本机保存最近 20 次草稿</p>
            </div>
          </header>
          {history.length ? (
            <div>
              {history.map((record) => (
                <article key={record.id}>
                  <History />
                  <span>
                    <strong>{record.productName}</strong>
                    <small>
                      {new Date(record.createdAt).toLocaleString("zh-CN")} ·{" "}
                      {record.count} 条评价 · 每组 {record.imageCount} 张
                    </small>
                    <em
                      className={`history-status ${record.status ?? "completed"}`}
                    >
                      {record.status === "running"
                        ? "生成中"
                        : record.status === "paused"
                          ? "已暂停"
                          : record.status === "partial"
                            ? "部分完成"
                            : record.status === "failed"
                              ? "生成失败"
                              : "已完成"}
                    </em>
                  </span>
                  <button
                    onClick={() => {
                      setProductName(record.productName);
                      setBrandName(record.brandName ?? "");
                      setCategory(record.category ?? "美妆个护");
                      setAudience(record.audience ?? "");
                      setBenefits(record.benefits ?? "");
                      setAssets(record.assets ?? []);
                      setReferenceAssetIds(
                        record.referenceAssetIds ??
                          (record.assets ?? [])
                            .slice(0, 4)
                            .map((asset) => asset.id),
                      );
                      setReviewCount(record.count);
                      setReviewImageCount(record.imageCount);
                      const restoredDirections = record.selectedDirections
                        ?.length
                        ? record.selectedDirections
                        : selectedDirections;
                      setSelectedDirections(restoredDirections);
                      setAuthorized(record.authorized ?? authorized);
                      setCompetitorUrl(record.competitorUrl ?? "");
                      setCompetitorAnalysis(
                        record.competitorAnalysis?.source ===
                          "pinduoduo-authorized-shop"
                          ? record.competitorAnalysis
                          : null,
                      );
                      setPublicProductReviewAnalysis(
                        record.publicProductReviewAnalysis?.source ===
                          "pinduoduo-public-product"
                          ? record.publicProductReviewAnalysis
                          : null,
                      );
                      setReviewRequirements(
                        record.reviewRequirements ?? defaultReviewRequirements,
                      );
                      setEndorsementSettings(
                        restoreReviewEndorsementSettings(
                          record.endorsementSettings,
                        ),
                      );
                      setGenerationRequirements(
                        record.generationRequirements ??
                          defaultGenerationRequirements,
                      );
                      const restoredLockGroupModel =
                        record.lockGroupModel ?? true;
                      setLockGroupModel(restoredLockGroupModel);
                      if (record.directionRequirements)
                        setDirectionRequirements({
                          ...defaultDirectionRequirements,
                          ...record.directionRequirements,
                        });
                      if (record.directionContent)
                        setDirectionContent({
                          ...defaultDirectionContent,
                          ...record.directionContent,
                        });
                      const batchId = record.id || `${Date.now()}`;
                      const restoredDrafts = ensureReviewGroupModelIdentities(
                        restoreReviewDraftsForManualContinuation(
                          record.drafts?.length
                            ? record.drafts
                            : record.reviews.map((text, index) => {
                                const allocated = allocateDirections(
                                  restoredDirections,
                                  record.imageCount,
                                );
                                return {
                                  id: `${batchId}-${index}`,
                                  batchId,
                                  text,
                                  createdAt: record.createdAt,
                                  directions: [...new Set(allocated)],
                                  imageCount: record.imageCount,
                                  imageSlots: allocated.map(
                                    (direction, imageIndex) => ({
                                      id: `${batchId}-${index}-${imageIndex}`,
                                      direction,
                                      status: "waiting" as const,
                                      image: null,
                                      error: null,
                                    }),
                                  ),
                                };
                              }),
                        ),
                        restoredLockGroupModel,
                      );
                      setDrafts(restoredDrafts);
                      setSelectedDraftIds([]);
                      const incomplete = restoredDrafts.some((draft) =>
                        draft.imageSlots.some(
                          (slot) => slot.status !== "ready",
                        ),
                      );
                      const restoredTotal = restoredDrafts.reduce(
                        (total, draft) => total + draft.imageSlots.length,
                        0,
                      );
                      const restoredCompleted = restoredDrafts.reduce(
                        (total, draft) =>
                          total +
                          draft.imageSlots.filter(
                            (slot) => slot.status === "ready",
                          ).length,
                        0,
                      );
                      setReviewProgress({
                        completed: restoredCompleted,
                        total: restoredTotal,
                        active: 0,
                        waiting: Math.max(0, restoredTotal - restoredCompleted),
                        failed: 0,
                        concurrency: 0,
                        startedAt: 0,
                      });
                      generationPausedRef.current = incomplete;
                      setGenerationPaused(incomplete);
                      setBranch("reviews");
                    }}
                  >
                    {record.status === "completed" ? "复用" : "继续"}
                  </button>
                  <button
                    title="删除"
                    onClick={() => {
                      const next = history.filter(
                        (item) => item.id !== record.id,
                      );
                      replaceHistory(() => next);
                    }}
                  >
                    <Trash2 />
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="review-result-empty">
              <History />
              <strong>暂无生成记录</strong>
              <span>生成图文草稿后自动保存在这里</span>
            </div>
          )}
        </section>
      )}

      {providerOpen && canManageProvider && (
        <div
          className="studio-provider-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setProviderOpen(false);
          }}
        >
          <section
            className="studio-provider-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="studio-provider-title"
          >
            <header>
              <div>
                <span>
                  <KeyRound />
                </span>
                <div>
                  <h3 id="studio-provider-title">配置 GPT Image 2 接口</h3>
                  <p>API Key 加密保存在本机服务端</p>
                </div>
              </div>
              <button
                type="button"
                title="关闭"
                onClick={() => setProviderOpen(false)}
              >
                <X />
              </button>
            </header>
            <div className="studio-provider-form">
              {showProviderSetupError && (
                <div className="studio-provider-setup-state" role="status">
                  <CircleAlert />
                  <span>
                    <strong>图片生成尚未开始</strong>
                    <small>
                      填写 API Key 后再保存；连接检测最长约 10
                      秒，不会生成正式图片。
                    </small>
                  </span>
                </div>
              )}
              <label>
                渠道名称
                <input
                  value={providerDraft.label}
                  onChange={(event) =>
                    setProviderDraft((current) => ({
                      ...current,
                      label: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                <span className="studio-provider-field-title">
                  API Key <small>必填</small>
                </span>
                <input
                  type="password"
                  autoFocus={!configuredProvider?.hasApiKey}
                  autoComplete="new-password"
                  aria-required="true"
                  aria-invalid={Boolean(showProviderSetupError && imageError)}
                  value={providerDraft.apiKey}
                  placeholder={
                    configuredProvider?.hasApiKey
                      ? "已保存，留空表示不修改"
                      : "输入平台 API Key"
                  }
                  onChange={(event) =>
                    setProviderDraft((current) => ({
                      ...current,
                      apiKey: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                生成接口地址
                <input
                  value={providerDraft.baseUrl}
                  onChange={(event) =>
                    setProviderDraft((current) => ({
                      ...current,
                      baseUrl: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                模型名称
                <input
                  value={providerDraft.model}
                  onChange={(event) =>
                    setProviderDraft((current) => ({
                      ...current,
                      model: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="studio-provider-enabled">
                <input
                  type="checkbox"
                  checked={providerDraft.enabled}
                  onChange={(event) =>
                    setProviderDraft((current) => ({
                      ...current,
                      enabled: event.target.checked,
                    }))
                  }
                />
                <span>启用此渠道</span>
              </label>
              {imageError && (
                <div className="studio-provider-error">
                  <X />
                  {imageError}
                </div>
              )}
            </div>
            <footer>
              <button
                type="button"
                className="studio-provider-test"
                disabled={testProvider.isPending || saveProvider.isPending}
                onClick={() => submitProviderConfiguration("test")}
              >
                {testProvider.isPending ? (
                  <RefreshCw className="spinning" />
                ) : (
                  <PlugZap />
                )}
                {testProvider.isPending
                  ? "正在检测（最长 10 秒）"
                  : "保存并检测"}
              </button>
              <button
                type="button"
                className="studio-provider-save"
                disabled={saveProvider.isPending || testProvider.isPending}
                onClick={() => submitProviderConfiguration("save")}
              >
                {saveProvider.isPending ? (
                  <RefreshCw className="spinning" />
                ) : (
                  <Save />
                )}
                {saveProvider.isPending ? "正在保存" : "保存配置"}
              </button>
            </footer>
          </section>
        </div>
      )}

      {modelTemplateOpen && (
        <div
          className="model-template-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setModelTemplateOpen(false);
          }}
        >
          <section className="model-template-dialog" role="dialog" aria-modal="true">
            <header>
              <div>
                <span>
                  <UserRound />
                </span>
                <div>
                  <h3>人物模板池（50 套）</h3>
                  <p>每套外貌固定对应一套穿搭，新评价组自动轮换</p>
                </div>
              </div>
              <button
                type="button"
                title="关闭"
                onClick={() => setModelTemplateOpen(false)}
              >
                <X />
              </button>
            </header>
            <div className="model-template-list">
              {reviewModelTemplates.map((template) => (
                <article key={template.id}>
                  <strong>
                    人物 {String(template.id).padStart(2, "0")}
                  </strong>
                  <dl>
                    <div>
                      <dt>外貌档案</dt>
                      <dd>{template.profile}</dd>
                    </div>
                    <div>
                      <dt>固定穿搭</dt>
                      <dd>{template.wardrobe}</dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          </section>
        </div>
      )}

      {referenceOpen && (
        <div
          className="reference-dialog-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setReferenceOpen(false);
          }}
        >
          <section className="reference-dialog" role="dialog" aria-modal="true">
            <header>
              <div>
                <h3>同行参考链接</h3>
                <p>保存同行商品、买家秀或内容页面</p>
              </div>
              <button title="关闭" onClick={() => setReferenceOpen(false)}>
                <X />
              </button>
            </header>
            <div className="reference-form">
              <label>
                参考名称
                <input
                  value={referenceDraft.name}
                  onChange={(event) =>
                    setReferenceDraft((value) => ({
                      ...value,
                      name: event.target.value,
                    }))
                  }
                  placeholder="例如：同品类高赞买家秀"
                />
              </label>
              <label>
                页面链接
                <input
                  type="url"
                  value={referenceDraft.url}
                  onChange={(event) =>
                    setReferenceDraft((value) => ({
                      ...value,
                      url: event.target.value,
                    }))
                  }
                  placeholder="https://"
                />
              </label>
              <label>
                备注
                <textarea
                  value={referenceDraft.note}
                  onChange={(event) =>
                    setReferenceDraft((value) => ({
                      ...value,
                      note: event.target.value,
                    }))
                  }
                  placeholder="记录需要参考的构图、场景或表达"
                />
              </label>
              <button
                disabled={!referenceDraft.url.trim()}
                onClick={addReference}
              >
                <Plus />
                添加参考
              </button>
            </div>
            <div className="reference-list">
              {references.map((reference) => (
                <article key={reference.id}>
                  <Link2 />
                  <span>
                    <strong>{reference.name}</strong>
                    <small>{reference.note || reference.url}</small>
                  </span>
                  <a
                    href={reference.url}
                    target="_blank"
                    rel="noreferrer"
                    title="打开链接"
                  >
                    <ExternalLink />
                  </a>
                  <button
                    title="删除"
                    onClick={() => {
                      const next = references.filter(
                        (item) => item.id !== reference.id,
                      );
                      setReferences(next);
                      localStorage.setItem(
                        "jianze-review-references-v1",
                        JSON.stringify(next),
                      );
                    }}
                  >
                    <Trash2 />
                  </button>
                </article>
              ))}
              {!references.length && <p>暂无参考链接</p>}
            </div>
            <footer>
              <button onClick={() => setReferenceOpen(false)}>
                <Save />
                完成
              </button>
            </footer>
          </section>
        </div>
      )}
      {selectedLibraryItem && (
        <div
          className="review-library-detail-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget)
              setSelectedLibraryId(null);
          }}
        >
          <section
            className="review-library-detail"
            role="dialog"
            aria-modal="true"
          >
            <header>
              <div>
                <strong>{selectedLibraryItem.productName}</strong>
                <small>
                  {new Date(selectedLibraryItem.savedAt).toLocaleString(
                    "zh-CN",
                  )}{" "}
                  · {selectedLibraryItem.images.length} 张原图
                </small>
              </div>
              <button
                type="button"
                title="关闭"
                onClick={() => setSelectedLibraryId(null)}
              >
                <X />
              </button>
            </header>
            <div className="review-library-detail-images">
              {selectedLibraryItem.images.map((image, index) => (
                <article key={image.id}>
                  <button
                    type="button"
                    title="点击放大"
                    onClick={() =>
                      setPreviewImage({
                        gallery: selectedLibraryItem.images,
                        title: selectedLibraryItem.productName,
                        index,
                      })
                    }
                  >
                    <img
                      src={image.url}
                      alt={`${selectedLibraryItem.productName} ${index + 1}`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      void downloadGeneratedImage(image.url, index)
                    }
                  >
                    <Download />
                    下载原图
                  </button>
                </article>
              ))}
            </div>
            <div className="review-library-detail-copy">
              <p>{selectedLibraryItem.reviewText}</p>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(
                    selectedLibraryItem.reviewText,
                  );
                  setCopiedId(selectedLibraryItem.id);
                  window.setTimeout(() => setCopiedId(null), 1_500);
                }}
              >
                {copiedId === selectedLibraryItem.id ? (
                  <Check />
                ) : (
                  <Clipboard />
                )}
                {copiedId === selectedLibraryItem.id ? "已复制" : "复制评语"}
              </button>
            </div>
            <footer>
              <span>{selectedLibraryItem.directions.join(" · ")}</span>
              <button
                type="button"
                disabled={archiveAction !== null}
                onClick={() =>
                  void downloadReviewArchive(
                    [selectedLibraryItem],
                    `library-${selectedLibraryItem.id}`,
                  )
                }
              >
                {archiveAction === `library-${selectedLibraryItem.id}` ? (
                  <RefreshCw className="spinning" />
                ) : (
                  <Download />
                )}
                {archiveAction === `library-${selectedLibraryItem.id}`
                  ? "打包中"
                  : "下载本组 ZIP"}
              </button>
            </footer>
          </section>
        </div>
      )}
      {previewImage && (
        <div
          className="review-image-preview-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPreviewImage(null);
          }}
        >
          <section
            className="review-image-preview"
            role="dialog"
            aria-modal="true"
          >
            <header>
              <strong>
                {previewImage.title} · {previewImage.index + 1}/
                {previewImage.gallery.length}
              </strong>
              <div>
                <button
                  type="button"
                  title="下载原图"
                  onClick={() =>
                    void downloadGeneratedImage(
                      previewImage.gallery[previewImage.index]!.url,
                      previewImage.index,
                    )
                  }
                >
                  <Download />
                </button>
                <button
                  type="button"
                  title="关闭"
                  onClick={() => setPreviewImage(null)}
                >
                  <X />
                </button>
              </div>
            </header>
            <div className="review-image-preview-stage">
              <button
                type="button"
                className="previous"
                title="上一张"
                disabled={previewImage.gallery.length < 2}
                onClick={() =>
                  setPreviewImage((current) =>
                    current
                      ? {
                          ...current,
                          index:
                            (current.index - 1 + current.gallery.length) %
                            current.gallery.length,
                        }
                      : null,
                  )
                }
              >
                <ChevronLeft />
              </button>
              <img
                src={previewImage.gallery[previewImage.index]!.url}
                alt={previewImage.title}
              />
              <button
                type="button"
                className="next"
                title="下一张"
                disabled={previewImage.gallery.length < 2}
                onClick={() =>
                  setPreviewImage((current) =>
                    current
                      ? {
                          ...current,
                          index: (current.index + 1) % current.gallery.length,
                        }
                      : null,
                  )
                }
              >
                <ChevronRight />
              </button>
            </div>
          </section>
        </div>
      )}
      {imageNotice && (
        <div className="studio-image-toast">
          <Check />
          {imageNotice}
        </div>
      )}
    </>
  );
}
